import React, { useEffect, useMemo, useRef, useState } from "react";

const PROJECTIONS_URL = "/data/nfl/showdown/latest/projections.json";
const VEGAS_URL = "/data/nfl/classic/latest/vegas.json";
const SITE_IDS_URL = "/data/nfl/showdown/latest/site_ids.json";
const PLAYER_SIMULATIONS_URL = "/data/nfl/classic/latest/playersimulations.json";
const DEFAULT_GAME_LOG_YEAR = 2026;
const GAME_LOG_YEARS = [2026, 2025];
const gameLogUrl = (year, position) =>
  `/data/nfl/std/${year}/game_logs_${String(position || "QB").toLowerCase()}.json`;

const opportunityUrl = (year) => `/data/nfl/std/${year}/ff_opportunity.json`;

// Match the positional-data page: lazy-load once per position and reuse the prepared rows.
const GAME_LOG_PREPARED_CACHE = new Map();
const GAME_LOG_REQUEST_CACHE = new Map();
const FILE_ROWS_CACHE = new Map();
const FILE_REQUEST_CACHE = new Map();
const LOGO_BASE = "/logos/nfl";
const LOGO_EXT = "png";


const SITE_CONFIG = {
  dk: {
    label: "DraftKings",
    short: "DK",
    logo: "/logos/dk.png",
    cap: 50000,
    multiplierTag: "CPT",
    salaryKeys: ["DK FLEX Sal", "DK FLEX Salary", "DK Sal", "DK Salary", "dk_sal", "dk salary"],
    multiplierSalaryKeys: ["DK CPT Sal", "DK CPT Salary", "dk_cpt_sal"],
    projectionKeys: ["DK FLEX Proj", "DK Proj", "DK Projection", "dk_proj", "Projection", "Proj"],
    multiplierProjectionKeys: ["DK CPT Proj", "DK CPT Projection", "dk_cpt_proj"],
    ownershipKeys: ["DK FLEX pOWN%", "DK FLEX pOWN", "DK pOWN%", "DK pOWN", "DK Own%", "DK Ownership"],
    multiplierOwnershipKeys: ["DK CPT pOWN%", "DK CPT pOWN", "DK CPT Own%"],
    optimalKeys: ["DK FLEX Opt%", "DK FLEX Opt", "DK Opt%", "DK Opt", "DK Optimal%", "DK Optimal Rate"],
    multiplierOptimalKeys: ["DK CPT Opt%", "DK CPT Opt", "DK CPT Optimal%"],
    floorKeys: ["DK Floor", "Floor"],
    ceilingKeys: ["DK Ceiling", "Ceiling"],
    slots: [
      { id: "CPT", label: "CPT", eligible: ["QB", "RB", "WR", "TE", "K", "DST"], multiplier: true },
      { id: "FLEX1", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX2", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX3", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX4", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX5", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
    ],
  },
  fd: {
    label: "FanDuel",
    short: "FD",
    logo: "/logos/fd.png",
    cap: 60000,
    multiplierTag: "MVP",
    salaryKeys: ["FD FLEX Sal", "FD FLEX Salary", "FD Sal", "FD Salary", "fd_sal", "fd salary"],
    multiplierSalaryKeys: ["FD MVP Sal", "FD MVP Salary", "fd_mvp_sal"],
    projectionKeys: ["FD FLEX Proj", "FD Proj", "FD Projection", "fd_proj", "Projection", "Proj"],
    multiplierProjectionKeys: ["FD MVP Proj", "FD MVP Projection", "fd_mvp_proj"],
    ownershipKeys: ["FD FLEX pOWN%", "FD FLEX pOWN", "FD pOWN%", "FD pOWN", "FD Own%", "FD Ownership"],
    multiplierOwnershipKeys: ["FD MVP pOWN%", "FD MVP pOWN", "FD MVP Own%"],
    optimalKeys: ["FD FLEX Opt%", "FD FLEX Opt", "FD Opt%", "FD Opt", "FD Optimal%", "FD Optimal Rate"],
    multiplierOptimalKeys: ["FD MVP Opt%", "FD MVP Opt", "FD MVP Optimal%"],
    floorKeys: ["FD Floor", "Floor"],
    ceilingKeys: ["FD Ceiling", "Ceiling"],
    slots: [
      { id: "MVP", label: "MVP", eligible: ["QB", "RB", "WR", "TE", "K", "DST"], multiplier: true },
      { id: "FLEX1", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX2", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX3", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX4", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
      { id: "FLEX5", label: "FLEX", eligible: ["QB", "RB", "WR", "TE", "K", "DST"] },
    ],
  },
};

const TEAM_COLORS = {
  ARI: "#97233F", ATL: "#A71930", BAL: "#241773", BUF: "#00338D", CAR: "#0085CA",
  CHI: "#0B162A", CIN: "#FB4F14", CLE: "#311D00", DAL: "#041E42", DEN: "#FB4F14",
  DET: "#0076B6", GB: "#203731", HOU: "#03202F", IND: "#002C5F", JAX: "#006778",
  KC: "#E31837", LAC: "#0080C6", LAR: "#003594", LV: "#000000", MIA: "#008E97",
  MIN: "#4F2683", NE: "#002244", NO: "#D3BC8D", NYG: "#0B2265", NYJ: "#125740",
  PHI: "#004C54", PIT: "#FFB612", SEA: "#002244", SF: "#AA0000", TB: "#D50A0A",
  TEN: "#0C2340", WAS: "#5A1414",
};

const TEAM_FIX = { WSH: "WAS", JAC: "JAX", OAK: "LV", SD: "LAC", STL: "LAR" };
const POSITIONS = ["ALL", "QB", "RB", "WR", "TE", "K", "DST"];

function normText(value) {
  return String(value ?? "").trim();
}
function normKey(value) {
  return normText(value).toLowerCase().replace(/[%\s._()-]/g, "");
}
function normName(value) {
  return normText(value)
    .toLowerCase()
    .replace(/[.’]/g, "")
    .replace(/,?\s+(jr|sr|ii|iii|iv)$/i, "")
    .replace(/[^a-z0-9]/g, "");
}
function fixTeam(value) {
  const team = normText(value).toUpperCase();
  return TEAM_FIX[team] || team;
}
function normPos(value) {
  const raw = normText(value).toUpperCase();
  if (["D", "DEF", "DEFENSE", "D/ST", "DST"].includes(raw)) return "DST";
  return raw.replace("/FLEX", "");
}
function numberLike(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/[$,%\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}
function asRate(value) {
  const n = numberLike(value);
  return n > 1.5 ? n / 100 : n;
}
function firstValue(row, aliases, fallback = null) {
  if (!row) return fallback;
  const index = new Map(Object.keys(row).map((key) => [normKey(key), key]));
  for (const alias of aliases) {
    const key = index.get(normKey(alias));
    if (key != null && row[key] != null && row[key] !== "") return row[key];
  }
  return fallback;
}
function fmtMoney(value) {
  return `$${Math.round(numberLike(value)).toLocaleString()}`;
}
function fmt1(value) {
  return value != null && value !== "" && Number.isFinite(Number(value)) ? Number(value).toFixed(1) : "—";
}
function fmtPct(value) {
  return value != null && value !== "" && Number.isFinite(Number(value)) ? `${(Number(value) * 100).toFixed(1)}%` : "—";
}

function fmtSpread(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(1).replace(/\.0$/, "");
  if (n > 0) return `+${body}`;
  if (n < 0) return `-${body}`;
  return "PK";
}
function parseKickoffMinutes(value) {
  const raw = normText(value);
  if (!raw) return Number.POSITIVE_INFINITY;

  const amPm = raw.match(/(\d{1,2}):(\d{2})\s*([AP])M/i);
  if (amPm) {
    let hour = Number(amPm[1]) % 12;
    if (/p/i.test(amPm[3])) hour += 12;
    return hour * 60 + Number(amPm[2]);
  }

  const military = raw.match(/^(\d{1,2}):(\d{2})/);
  if (military) return Number(military[1]) * 60 + Number(military[2]);
  return Number.POSITIVE_INFINITY;
}
function explicitBoolean(value) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const raw = String(value).trim().toLowerCase();

  if (["true", "yes", "y", "home", "h", "1"].includes(raw)) return true;
  if (["false", "no", "n", "away", "a", "0"].includes(raw)) return false;

  // Also support numeric strings such as "1.0" and "0.0".
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric !== 0;

  return null;
}
function getHomeTeam(row) {
  const team = fixTeam(firstValue(row, ["Team", "team", "Tm", "TEAM"], ""));
  const opp = fixTeam(firstValue(row, ["OPP", "Opp", "opp", "Opponent", "opponent"], ""));
  const homeRaw = firstValue(
    row,
    ["Home", "home", "Home Team", "home_team", "Is Home", "is_home"],
    null
  );

  if (homeRaw == null || homeRaw === "") return "";

  // Current NFL JSONs store the actual home-team abbreviation, e.g. Home: "IND".
  const homeCode = fixTeam(homeRaw);
  if (homeCode && (homeCode === team || homeCode === opp)) return homeCode;

  // Retain compatibility with feeds that use a boolean-style home indicator.
  const flag = explicitBoolean(homeRaw);
  if (flag === true) return team;
  if (flag === false) return opp;

  return "";
}

function geometricMean(values) {
  const valid = values.filter((v) => Number.isFinite(v) && v > 0);
  if (!valid.length) return 0;
  return Math.exp(valid.reduce((sum, v) => sum + Math.log(v), 0) / valid.length);
}
async function fetchRows(url, { fresh = false } = {}) {
  if (!fresh && FILE_ROWS_CACHE.has(url)) return FILE_ROWS_CACHE.get(url);
  if (!fresh && FILE_REQUEST_CACHE.has(url)) return FILE_REQUEST_CACHE.get(url);

  const separator = url.includes("?") ? "&" : "?";
  const requestUrl = fresh ? `${url}${separator}_=${Date.now()}` : url;

  const request = fetch(requestUrl, {
    cache: fresh ? "no-store" : "force-cache",
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
      return response.json();
    })
    .then((json) => {
      const rows = Array.isArray(json) ? json : json?.rows || json?.data || json?.players || [];
      FILE_ROWS_CACHE.set(url, rows);
      FILE_REQUEST_CACHE.delete(url);
      return rows;
    })
    .catch((error) => {
      FILE_REQUEST_CACHE.delete(url);
      throw error;
    });

  FILE_REQUEST_CACHE.set(url, request);
  return request;
}

async function fetchJsonFresh(url) {
  const separator = url.includes("?") ? "&" : "?";
  const response = await fetch(`${url}${separator}_=${Date.now()}`, {
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
  return response.json();
}
const NFL_SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);
function normalizeLogName(name = "") {
  const parts = String(name)
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (parts.length > 1 && NFL_SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return parts.join(" ");
}
function logName(r = {}) {
  return firstValue(r, ["full_name", "player_display_name", "player_name", "pfr_player_name", "player", "name"], "");
}
function logPos(r = {}) {
  return normPos(firstValue(r, ["position", "player_position", "position_group"], ""));
}
function logTeam(r = {}) {
  return fixTeam(firstValue(r, ["posteam", "recent_team", "team_abbr", "team"], ""));
}
function logSeason(r = {}) { return numberLike(firstValue(r, ["season", "SEASON"], 0)); }
function logWeek(r = {}) { return numberLike(firstValue(r, ["week", "WEEK"], 0)); }
function logIds(r = {}) {
  return [
    firstValue(r, ["player_id", "player_gsis_id", "gsis_id", "PLAYER_GSIS_ID"], ""),
    firstValue(r, ["pfr_player_id", "pfr_id"], ""),
  ].map(v => String(v || "").trim().toLowerCase()).filter(Boolean);
}
function logOpponent(r = {}) {
  const direct = firstValue(r, ["opponent", "opp", "opponent_team"], "");
  if (direct) return fixTeam(direct);
  const parts = String(r?.game_id || "").split("_");
  if (parts.length < 4) return "";
  const away = fixTeam(parts[parts.length - 2]);
  const home = fixTeam(parts[parts.length - 1]);
  const team = logTeam(r);
  return team === away ? home : team === home ? away : "";
}
function calcDkPoints(r = {}) {
  const py = numberLike(firstValue(r, ["pass_yards_gained", "passing_yards"]));
  const ptd = numberLike(firstValue(r, ["pass_touchdown", "passing_tds"]));
  const pi = numberLike(firstValue(r, ["pass_interception", "passing_interceptions"]));
  const ry = numberLike(firstValue(r, ["rush_yards_gained", "rushing_yards"]));
  const rtd = numberLike(firstValue(r, ["rush_touchdown", "rushing_tds"]));
  const recy = numberLike(firstValue(r, ["rec_yards_gained", "receiving_yards"]));
  const rectd = numberLike(firstValue(r, ["rec_touchdown", "receiving_tds"]));
  const rec = numberLike(firstValue(r, ["receptions"]));
  const two = numberLike(firstValue(r, ["pass_two_point_conv", "passing_2pt_conversions"])) + numberLike(firstValue(r, ["rush_two_point_conv", "rushing_2pt_conversions"])) + numberLike(firstValue(r, ["rec_two_point_conv", "receiving_2pt_conversions"]));
  const fl = numberLike(firstValue(r, ["fumbles_lost"])) + numberLike(firstValue(r, ["rec_fumble_lost", "receiving_fumbles_lost"])) + numberLike(firstValue(r, ["rush_fumble_lost", "rushing_fumbles_lost"]));
  return py*.04 + ptd*4 - pi + ry*.1 + rtd*6 + recy*.1 + rectd*6 + rec + two*2 - fl + (py>=300?3:0) + (ry>=100?3:0) + (recy>=100?3:0);
}
function calcFdPoints(r = {}) {
  const py = numberLike(firstValue(r, ["pass_yards_gained", "passing_yards"]));
  const ptd = numberLike(firstValue(r, ["pass_touchdown", "passing_tds"]));
  const pi = numberLike(firstValue(r, ["pass_interception", "passing_interceptions"]));
  const ry = numberLike(firstValue(r, ["rush_yards_gained", "rushing_yards"]));
  const rtd = numberLike(firstValue(r, ["rush_touchdown", "rushing_tds"]));
  const recy = numberLike(firstValue(r, ["rec_yards_gained", "receiving_yards"]));
  const rectd = numberLike(firstValue(r, ["rec_touchdown", "receiving_tds"]));
  const rec = numberLike(firstValue(r, ["receptions"]));
  const two = numberLike(firstValue(r, ["pass_two_point_conv", "passing_2pt_conversions"])) + numberLike(firstValue(r, ["rush_two_point_conv", "rushing_2pt_conversions"])) + numberLike(firstValue(r, ["rec_two_point_conv", "receiving_2pt_conversions"]));
  const fl = numberLike(firstValue(r, ["fumbles_lost"])) + numberLike(firstValue(r, ["rec_fumble_lost", "receiving_fumbles_lost"])) + numberLike(firstValue(r, ["rush_fumble_lost", "rushing_fumbles_lost"]));
  return py*.04 + ptd*4 - pi + ry*.1 + rtd*6 + recy*.1 + rectd*6 + rec*.5 + two*2 - fl*2 + (py>=300?3:0) + (ry>=100?3:0) + (recy>=100?3:0);
}
function mergeWeeklySources(baseRows = [], detailRows = []) {
  const idIndex = new Map(), nameIndex = new Map();
  const push = (map, key, row) => { if (!key) return; if (!map.has(key)) map.set(key, []); map.get(key).push(row); };
  for (const row of detailRows) {
    const season=logSeason(row), week=logWeek(row); if (!season || !week) continue;
    const team=logTeam(row), name=normalizeLogName(logName(row));
    for (const id of logIds(row)) push(idIndex, `${season}__${week}__${id}`, row);
    if (name) { push(nameIndex, `${season}__${week}__${team}__${name}`, row); push(nameIndex, `${season}__${week}____${name}`, row); }
  }
  return baseRows.map(base => {
    const season=logSeason(base), week=logWeek(base), team=logTeam(base), name=normalizeLogName(logName(base));
    const matches=[], seen=new Set(); const add=rows => (rows||[]).forEach(row => { if(!seen.has(row)){seen.add(row);matches.push(row);} });
    for (const id of logIds(base)) add(idIndex.get(`${season}__${week}__${id}`));
    add(nameIndex.get(`${season}__${week}__${team}__${name}`)); add(nameIndex.get(`${season}__${week}____${name}`));
    return matches.reduce((merged, extra) => ({...extra, ...merged}), {...base});
  });
}
function normalizeLogRow(r = {}) {
  const passAtt = numberLike(firstValue(r, ["pass_attempt", "passing_attempts", "attempts"]));
  const passCmp = numberLike(firstValue(r, ["pass_completions", "pass_completion", "passing_completions", "completions"]));
  const passYds = numberLike(firstValue(r, ["pass_yards_gained", "passing_yards"]));
  const rushAtt = numberLike(firstValue(r, ["rush_attempt", "rushing_attempts", "carries"]));
  const rushYds = numberLike(firstValue(r, ["rush_yards_gained", "rushing_yards"]));
  const rec = numberLike(firstValue(r, ["receptions"]));
  const recYds = numberLike(firstValue(r, ["rec_yards_gained", "receiving_yards"]));
  const targets = numberLike(firstValue(r, ["rec_attempt", "targets"]));
  const airYards = numberLike(firstValue(r, ["rec_air_yards", "receiving_air_yards", "air_yards"]));
  const tgtShareRaw = firstValue(r, ["target_share", "tgt_share"], null);
  const airShareRaw = firstValue(r, ["air_yards_share", "air_share"], null);
  const snapRaw = firstValue(r, ["offense_pct", "snap_pct", "snap_share"], null);

  return {
    raw: r,
    season: logSeason(r),
    week: logWeek(r),
    seasonType: String(firstValue(r, ["season_type", "game_type"], "REG")),
    name: logName(r),
    pos: logPos(r),
    team: logTeam(r),
    opp: logOpponent(r),
    date: firstValue(r, ["gameday", "game_date", "date"], ""),
    passCmp,
    passAtt,
    passYds,
    passTd: numberLike(firstValue(r, ["pass_touchdown", "passing_tds"])),
    interceptions: numberLike(firstValue(r, ["pass_interception", "passing_interceptions"])),
    compPct: passAtt ? passCmp / passAtt : 0,
    ypa: passAtt ? passYds / passAtt : 0,
    sacks: numberLike(firstValue(r, ["sacks", "times_sacked", "sack"])),
    rushAtt,
    rushYds,
    rushTd: numberLike(firstValue(r, ["rush_touchdown", "rushing_tds"])),
    ypc: rushAtt ? rushYds / rushAtt : 0,
    targets,
    rec,
    recYds,
    recTd: numberLike(firstValue(r, ["rec_touchdown", "receiving_tds"])),
    ypr: rec ? recYds / rec : 0,
    airYards,
    targetShare: tgtShareRaw == null ? null : numberLike(tgtShareRaw),
    airShare: airShareRaw == null ? null : numberLike(airShareRaw),
    snapPct: snapRaw == null ? null : numberLike(snapRaw),
    fumblesLost:
      numberLike(firstValue(r, ["fumbles_lost"])) +
      numberLike(firstValue(r, ["rec_fumble_lost", "receiving_fumbles_lost"])) +
      numberLike(firstValue(r, ["rush_fumble_lost", "rushing_fumbles_lost"])),
    dk: Number(firstValue(r, ["dk_points", "dk_pts"], calcDkPoints(r))),
    fd: Number(firstValue(r, ["fd_points", "fd_pts"], calcFdPoints(r))),
  };
}

function prepareGameLogs(baseRows = [], detailRows = [], snapRows = [], scheduleRows = []) {
  const mergedRows = mergeWeeklySources(baseRows, detailRows);
  const normalized = mergedRows.map(normalizeLogRow);

  const teamTotals = new Map();
  for (const r of normalized) {
    const key = `${r.season}__${r.week}__${r.team}`;
    const cur = teamTotals.get(key) || { targets: 0, airYards: 0 };
    cur.targets += numberLike(r.targets);
    cur.airYards += numberLike(r.airYards);
    teamTotals.set(key, cur);
  }

  const snapMap = new Map();
  for (const r of snapRows || []) {
    const name = normalizeLogName(firstValue(r, ["player", "player_name", "full_name", "player_display_name", "name"], ""));
    const season = logSeason(r);
    const week = logWeek(r);
    const team = fixTeam(firstValue(r, ["team", "team_abbr", "recent_team", "posteam"], ""));
    let snapPct = firstValue(r, ["offense_pct", "offense_percentage", "snap_pct", "snap_share", "off_snap_pct"], null);
    if (snapPct == null) {
      const playerSnaps = numberLike(firstValue(r, ["offense_snaps", "off_snaps", "snaps"]));
      const teamSnaps = numberLike(firstValue(r, ["team_offense_snaps", "team_snaps", "offense_team_snaps"]));
      if (teamSnaps > 0) snapPct = playerSnaps / teamSnaps;
    }
    if (!name || !season || !week || snapPct == null) continue;
    snapMap.set(`${season}__${week}__${team}__${name}`, numberLike(snapPct));
    snapMap.set(`${season}__${week}____${name}`, numberLike(snapPct));
  }

  const scheduleMap = new Map();
  for (const game of scheduleRows || []) {
    const season = logSeason(game);
    const week = logWeek(game);
    const away = fixTeam(firstValue(game, ["away_team", "away", "visitor_team"], ""));
    const home = fixTeam(firstValue(game, ["home_team", "home"], ""));
    const awayScoreRaw = firstValue(game, ["away_score", "away_points", "visitor_score", "away_pts"], null);
    const homeScoreRaw = firstValue(game, ["home_score", "home_points", "home_pts"], null);
    const awayScore = awayScoreRaw == null || awayScoreRaw === "" ? null : Number(awayScoreRaw);
    const homeScore = homeScoreRaw == null || homeScoreRaw === "" ? null : Number(homeScoreRaw);
    if (!season || !week || !away || !home) continue;

    const makeContext = (opponent, teamScore, opponentScore) => {
      const scoresValid = Number.isFinite(teamScore) && Number.isFinite(opponentScore);
      const result = !scoresValid ? "" : teamScore > opponentScore ? "W" : teamScore < opponentScore ? "L" : "T";
      return {
        opponent,
        teamScore: scoresValid ? teamScore : null,
        opponentScore: scoresValid ? opponentScore : null,
        result,
      };
    };

    scheduleMap.set(`${season}__${week}__${away}`, makeContext(home, awayScore, homeScore));
    scheduleMap.set(`${season}__${week}__${home}`, makeContext(away, homeScore, awayScore));
  }

  return normalized.map((r) => {
    const wanted = normalizeLogName(r.name);
    const totals = teamTotals.get(`${r.season}__${r.week}__${r.team}`) || { targets: 0, airYards: 0 };
    const snapKey = `${r.season}__${r.week}__${r.team}__${wanted}`;
    const snapFallbackKey = `${r.season}__${r.week}____${wanted}`;
    const scheduleContext = scheduleMap.get(`${r.season}__${r.week}__${r.team}`) || {};

    return {
      ...r,
      _playerKey: wanted,
      opp: r.opp || scheduleContext.opponent || "",
      result: scheduleContext.result || "",
      teamScore: scheduleContext.teamScore ?? null,
      opponentScore: scheduleContext.opponentScore ?? null,
      targetShare: r.targetShare ?? (totals.targets > 0 ? r.targets / totals.targets : null),
      airShare: r.airShare ?? (totals.airYards !== 0 ? r.airYards / totals.airYards : null),
      snapPct: r.snapPct ?? snapMap.get(snapKey) ?? snapMap.get(snapFallbackKey) ?? null,
    };
  });
}

function useStickyState(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored == null ? initialValue : JSON.parse(stored);
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}

function TeamBadge({ team }) {
  const code = fixTeam(team);
  if (!code) return <span className="text-slate-600">—</span>;

  return (
    <span className="inline-flex h-7 w-9 shrink-0 items-center justify-center rounded-md bg-slate-950/60">
      <img
        src={`${LOGO_BASE}/${code}.${LOGO_EXT}`}
        alt={code}
        title={code}
        className="h-6 w-6 object-contain"
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    </span>
  );
}

function MetricCard({ label, value, tone = "default", subtext }) {
  const toneClass =
    tone === "good"
      ? "border-emerald-500/30 bg-emerald-500/10"
      : tone === "bad"
      ? "border-rose-500/30 bg-rose-500/10"
      : "border-slate-800 bg-slate-950/70";
  return (
    <div className={`rounded-lg border px-2 py-1 ${toneClass}`}>
      <div className="text-[8px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className="text-sm font-bold tabular-nums text-slate-100">{value}</div>
      {subtext ? <div className="text-[9px] leading-tight text-slate-500">{subtext}</div> : null}
    </div>
  );
}


function GameLogModal({
  player,
  preparedRows = [],
  loading,
  error,
  onClose,
  year,
  years,
  onYearChange,
}) {
  const position = player?.pos || "QB";
  const playerName = player?.name || "";
  const logs = useMemo(() => {
    const wanted = normalizeLogName(playerName);
    return preparedRows
      .filter((r) => r._playerKey === wanted && (!r.pos || r.pos === position || (position === "RB" && r.pos === "FB")))
      .sort((a, b) => a.season - b.season || a.week - b.week);
  }, [preparedRows, playerName, position]);

  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const one = (v) => {
    const n = Number(v);
    return v != null && v !== "" && Number.isFinite(n) ? n.toFixed(1).replace(/\.0$/, "") : "—";
  };
  const pct = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const n = Number(v);
    const x = Math.abs(n) <= 1.5 ? n * 100 : n;
    return `${x.toFixed(1).replace(/\.0$/, "")}%`;
  };
  const avg = (k) => {
    const values = logs
      .filter((r) => r[k] != null && r[k] !== "")
      .map((r) => Number(r[k]))
      .filter((v) => Number.isFinite(v));
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
  };

  const signed = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const n = Number(v);
    const body = Math.abs(n).toFixed(1).replace(/\.0$/, "");
    return n > 0 ? `+${body}` : n < 0 ? `-${body}` : "0";
  };

  const fpoeCell = (v) => {
    if (v == null || !Number.isFinite(Number(v))) {
      return <span className="text-slate-500">—</span>;
    }
    const n = Number(v);
    const tone =
      n > 0.05
        ? "text-emerald-400"
        : n < -0.05
        ? "text-rose-400"
        : "text-slate-300";

    return <span className={`font-extrabold ${tone}`}>{signed(n)}</span>;
  };

  const team = logs[0]?.team || player?.team || "";

  const TeamWithLogo = ({ code }) => {
    const c = fixTeam(code);
    if (!c) return null;
    return (
      <span className="inline-flex items-center gap-1">
        <img
          src={`${LOGO_BASE}/${c}.${LOGO_EXT}`}
          className="h-4 w-4 shrink-0"
          alt=""
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <span>{c}</span>
      </span>
    );
  };

  const common = [
    ["Season", (r) => r.season],
    ["Wk", (r) => r.week],
    ["Opp", (r) => (
      <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none">
        <TeamWithLogo code={r.opp} />
        {r.result && Number.isFinite(r.teamScore) && Number.isFinite(r.opponentScore) ? (
          <span
            className={`text-[9px] font-extrabold ${
              r.result === "W" ? "text-emerald-400" : r.result === "L" ? "text-red-400" : "text-slate-400"
            }`}
            title={`${r.result} ${r.teamScore}-${r.opponentScore}`}
          >
            {r.result} {r.teamScore}–{r.opponentScore}
          </span>
        ) : null}
      </div>
    )],
  ];
  const fantasy = [
    ["DK", (r) => one(r.dk)],
    ["FD", (r) => one(r.fd)],
    ["xFP", (r) => (r.xFp == null ? "—" : one(r.xFp))],
    ["FPOE", (r) => fpoeCell(r.fpDiff)],
  ];
  const passing = [
    ["Cmp", (r) => r.passCmp], ["Att", (r) => r.passAtt], ["Cmp%", (r) => pct(r.compPct)],
    ["Yds", (r) => r.passYds], ["TD", (r) => r.passTd],
    ["INT", (r) => r.interceptions], ["YPA", (r) => one(r.ypa)],
  ];
  const rushing = [
    ["Att", (r) => r.rushAtt], ["Yds", (r) => r.rushYds], ["TD", (r) => r.rushTd],
    ...(position === "RB" ? [["YPC", (r) => one(r.ypc)]] : []),
  ];
  const receiving = [
    ["Tgt", (r) => r.targets], ["Rec", (r) => r.rec], ["Yds", (r) => r.recYds],
    ["TD", (r) => r.recTd], ["YPR", (r) => one(r.ypr)],
    ...(position !== "RB" ? [["Air Yds", (r) => r.airYards]] : []),
  ];
  const usage = [
    ...(position === "RB" ? [["Rush + Tgts", (r) =>
      r.opportunities == null ? r.rushAtt + r.targets : r.opportunities,
      "Rushing attempts plus targets"]] : []),
    ["Tgt%", (r) => pct(r.targetShare), "Share of team targets"],
    ...(position !== "RB" ? [["Air%", (r) => pct(r.airShare), "Share of team air yards"]] : []),
    ["Snap%", (r) => pct(r.snapPct), "Offensive snap share"],
  ];
  const groups = [
    { label: "Game", tone: "text-slate-400 bg-slate-800/50", columns: common },
    ...(position === "QB" ? [
      { label: "Passing", tone: "text-sky-300 bg-sky-500/10", columns: passing },
      { label: "Rushing", tone: "text-emerald-300 bg-emerald-500/10", columns: rushing },
    ] : [
      ...(position === "RB" ? [{ label: "Rushing", tone: "text-emerald-300 bg-emerald-500/10", columns: rushing }] : []),
      { label: "Receiving", tone: "text-violet-300 bg-violet-500/10", columns: receiving },
      { label: "Usage", tone: "text-slate-300 bg-slate-800/60", columns: usage },
    ]),
    { label: "Fantasy", tone: "text-amber-300 bg-amber-500/10", columns: fantasy },
  ];
  const columns = groups.flatMap((group) => group.columns.map(([label, render, title], index) => ({
    label, render, title: title || `${group.label}: ${label}`, group: group.label, start: index === 0,
  })));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-2 md:p-5" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`${playerName} game log`} className="w-full max-w-[1500px] max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 text-slate-100 shadow-2xl">
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-white/10 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {team ? <img src={`${LOGO_BASE}/${team}.${LOGO_EXT}`} className="h-7 w-7 object-contain" alt="" onError={(e) => (e.currentTarget.style.display = "none")} /> : null}
              <h2 className="truncate text-lg font-extrabold md:text-2xl">{playerName}</h2>
              <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-400">{position}</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-400">{year} season <span className="mx-1 text-slate-600">/</span> Weekly game log <span className="mx-1 text-slate-600">/</span> Week 1 first</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className="flex h-9 items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/70 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Season
              </span>
              <select
                value={year}
                onChange={(event) => onYearChange(Number(event.target.value))}
                className="bg-transparent text-sm font-bold text-slate-100 outline-none"
              >
                {years.map((season) => (
                  <option key={season} value={season} className="bg-slate-900 text-slate-100">
                    {season}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-800 bg-slate-900/70"
              aria-label="Close game log"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 px-3 py-3 sm:grid-cols-4 md:px-5">
          {[
            ["Games", loading ? "—" : logs.length],
            ["Avg DK", one(avg("dk"))],
            ["Avg xFP", avg("xFp") == null ? "—" : one(avg("xFp"))],
            [
              position === "QB" ? "Avg Pass Yds" : position === "RB" ? "Rush + Tgts/G" : "Avg Targets",
              position === "QB"
                ? one(avg("passYds"))
                : position === "RB"
                ? one(
                    logs.length
                      ? logs.reduce(
                          (sum, r) =>
                            sum +
                            Number(
                              r.opportunities == null
                                ? r.rushAtt + r.targets
                                : r.opportunities
                            ),
                          0
                        ) / logs.length
                      : 0
                  )
                : one(avg("targets")),
            ],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
              <div className="text-lg font-extrabold leading-tight">{loading ? "—" : value}</div>
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t border-white/10">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading game logs…</div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-red-400">Unable to load game logs. Please try again.</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No weekly game logs found for this player.</div>
          ) : (
            <table className="w-max min-w-full border-separate border-spacing-0 text-[11px] tabular-nums md:text-xs">
              <caption className="sr-only">{playerName} weekly game log for {year}, earliest week first</caption>
              <thead className="sticky top-0 z-20 bg-slate-900">
                <tr>
                  {groups.map((group, index) => (
                    <th key={group.label} scope="colgroup" colSpan={group.columns.length}
                      className={`h-8 border-b border-white/10 px-3 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] ${group.tone} ${index ? "border-l border-l-slate-700" : ""}`}>
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map(({ label, title, group, start }, i) => (
                    <th key={`${group}-${label}-${i}`} scope="col" title={title}
                      className={`h-9 whitespace-nowrap border-b border-slate-700 bg-slate-900 px-3 text-center font-bold ${start && i ? "border-l border-l-slate-700" : ""} ${
                        label === "DK" || label === "FD" ? "text-sky-400" :
                        label === "xFP" || label === "FPOE" ? "text-amber-300" : "text-slate-300"
                      }`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r, ri) => (
                  <tr key={`${r.season}-${r.week}-${ri}`} className={`${ri % 2 ? "bg-slate-900/40" : "bg-slate-950"} transition-colors hover:bg-sky-500/10`}>
                    {columns.map(({ label, render, group, start }, ci) => (
                      <td key={`${group}-${label}-${ci}`}
                        className={`h-10 whitespace-nowrap border-b border-slate-800/70 px-3 text-center ${start && ci ? "border-l border-l-slate-700" : ""} ${
                          label === "DK" || label === "FD" ? "bg-sky-500/[0.04] font-extrabold text-sky-400" :
                          label === "xFP" ? "bg-amber-500/[0.04] font-extrabold text-amber-300" : ""
                        }`}>
                        {render(r) ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-white/10 bg-slate-900/60 px-4 py-2.5 text-[10px] leading-relaxed text-slate-400 md:px-5">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="font-bold text-amber-300">xFP</span> Expected fantasy points</span>
            <span><span className="font-bold text-amber-300">FPOE</span> Fantasy points over expected</span>
            <span><span className="text-emerald-400">+ Above</span> / <span className="text-rose-400">− Below</span> expected</span>
          </div>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}

function normalizePreparedGameLogRow(row = {}, fallbackPosition = "") {
  const position = normPos(firstValue(row, ["position", "pos"], fallbackPosition));
  const name = firstValue(row, ["player", "name", "player_name"], "");
  const team = fixTeam(firstValue(row, ["team", "posteam", "recent_team"], ""));

  return {
    raw: row,
    season: numberLike(firstValue(row, ["season"], 0)),
    week: numberLike(firstValue(row, ["week"], 0)),
    seasonType: String(firstValue(row, ["season_type", "game_type"], "REG")),
    playerId: String(firstValue(row, ["player_id", "player_gsis_id", "gsis_id"], "") || "").trim().toLowerCase(),
    name,
    pos: position,
    team,
    opp: fixTeam(firstValue(row, ["opponent", "opp", "opponent_team"], "")),
    result: String(firstValue(row, ["result"], "")),
    teamScore: firstValue(row, ["team_score"], null),
    opponentScore: firstValue(row, ["opponent_score"], null),
    _playerKey: normalizeLogName(firstValue(row, ["player_key", "player", "name"], name)),

    passCmp: numberLike(firstValue(row, ["pass_completions", "passCmp"], 0)),
    passAtt: numberLike(firstValue(row, ["pass_attempts", "passAtt"], 0)),
    passYds: numberLike(firstValue(row, ["passing_yards", "passYds"], 0)),
    passTd: numberLike(firstValue(row, ["passing_tds", "passTd"], 0)),
    interceptions: numberLike(firstValue(row, ["interceptions"], 0)),
    compPct: numberLike(firstValue(row, ["completion_pct", "compPct"], 0)),
    ypa: numberLike(firstValue(row, ["yards_per_attempt", "ypa"], 0)),
    sacks: numberLike(firstValue(row, ["sacks"], 0)),

    rushAtt: numberLike(firstValue(row, ["rushing_attempts", "rushAtt"], 0)),
    rushYds: numberLike(firstValue(row, ["rushing_yards", "rushYds"], 0)),
    rushTd: numberLike(firstValue(row, ["rushing_tds", "rushTd"], 0)),
    ypc: numberLike(firstValue(row, ["yards_per_carry", "ypc"], 0)),

    targets: numberLike(firstValue(row, ["targets"], 0)),
    rec: numberLike(firstValue(row, ["receptions", "rec"], 0)),
    recYds: numberLike(firstValue(row, ["receiving_yards", "recYds"], 0)),
    recTd: numberLike(firstValue(row, ["receiving_tds", "recTd"], 0)),
    ypr: numberLike(firstValue(row, ["yards_per_reception", "ypr"], 0)),
    airYards: numberLike(firstValue(row, ["air_yards", "airYards"], 0)),
    targetShare: firstValue(row, ["target_share", "targetShare"], null),
    airShare: firstValue(row, ["air_yards_share", "airShare"], null),
    snapPct: firstValue(row, ["snap_pct", "snapPct"], null),
    fumblesLost: numberLike(firstValue(row, ["fumbles_lost", "fumblesLost"], 0)),
    dk: numberLike(firstValue(row, ["dk_points", "dk"], 0)),
    fd: numberLike(firstValue(row, ["fd_points", "fd"], 0)),

    // Filled from ff_opportunity.json when available.
    xFp: null,
    fpDiff: null,
    opportunities: null,

    pointsAllowed: numberLike(firstValue(row, ["points_allowed"], 0)),
    fumbleRecoveries: numberLike(firstValue(row, ["fumble_recoveries"], 0)),
    defensiveTds: numberLike(firstValue(row, ["defensive_tds"], 0)),
  };
}

function nullableNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(String(value).replace(/[$,%\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function mergeOpportunityIntoGameLogs(logRows = [], opportunityRows = [], fallbackPosition = "") {
  const prepared = logRows.map((row) => normalizePreparedGameLogRow(row, fallbackPosition));

  const byId = new Map();
  const byName = new Map();

  for (const raw of opportunityRows || []) {
    const season = numberLike(firstValue(raw, ["season"], 0));
    const week = numberLike(firstValue(raw, ["week"], 0));
    if (!season || !week) continue;

    const playerId = String(
      firstValue(raw, ["player_id", "player_gsis_id", "gsis_id"], "") || ""
    ).trim().toLowerCase();

    const name = normalizeLogName(
      firstValue(raw, ["full_name", "player", "player_name", "name"], "")
    );
    const team = fixTeam(firstValue(raw, ["posteam", "team", "recent_team"], ""));

    const entry = {
      xFp: nullableNumber(
        firstValue(raw, ["total_fantasy_points_exp", "fantasy_points_exp", "xfp"], null)
      ),
      fpDiff: nullableNumber(
        firstValue(raw, ["total_fantasy_points_diff", "fantasy_points_diff", "fpoe"], null)
      ),
      opportunities:
        numberLike(firstValue(raw, ["rush_attempt", "rushing_attempts"], 0)) +
        numberLike(firstValue(raw, ["rec_attempt", "targets"], 0)),
    };

    if (playerId) {
      byId.set(`${season}__${week}__${playerId}`, entry);
    }

    if (name) {
      byName.set(`${season}__${week}__${team}__${name}`, entry);
      byName.set(`${season}__${week}____${name}`, entry);
    }
  }

  return prepared.map((row) => {
    let opportunity = null;

    if (row.playerId) {
      opportunity = byId.get(`${row.season}__${row.week}__${row.playerId}`) || null;
    }

    if (!opportunity) {
      opportunity =
        byName.get(`${row.season}__${row.week}__${row.team}__${row._playerKey}`) ||
        byName.get(`${row.season}__${row.week}____${row._playerKey}`) ||
        null;
    }

    if (!opportunity) return row;

    return {
      ...row,
      xFp: opportunity.xFp,
      fpDiff: opportunity.fpDiff,
      opportunities: opportunity.opportunities,
    };
  });
}

async function loadPreparedGameLogs(position, year) {
  const posKey = String(position || "QB").toUpperCase();
  const cacheKey = `${year}:${posKey}`;
  if (GAME_LOG_PREPARED_CACHE.has(cacheKey)) return GAME_LOG_PREPARED_CACHE.get(cacheKey);
  if (GAME_LOG_REQUEST_CACHE.has(cacheKey)) return GAME_LOG_REQUEST_CACHE.get(cacheKey);

  const request = Promise.all([
    fetchRows(gameLogUrl(year, posKey), { fresh: true }),
    // Keep the game log usable even if an older season has no opportunity file.
    fetchRows(opportunityUrl(year), { fresh: true }).catch(() => []),
  ])
    .then(([rows, opportunityRows]) => {
      const prepared = mergeOpportunityIntoGameLogs(rows, opportunityRows, posKey);
      GAME_LOG_PREPARED_CACHE.set(cacheKey, prepared);
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      return prepared;
    })
    .catch((error) => {
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      throw error;
    });

  GAME_LOG_REQUEST_CACHE.set(cacheKey, request);
  return request;
}

const SITES = { dk: { capTag: "CPT", flexFieldPrefix: "DK FLEX", mvpFieldPrefix: "DK CPT" }, fd: { capTag: "MVP", flexFieldPrefix: "FD FLEX", mvpFieldPrefix: "FD MVP" } };
const NFL_TEAMS = {
  ARI:{city:"Arizona",nickname:"Cardinals"}, ATL:{city:"Atlanta",nickname:"Falcons"},
  BAL:{city:"Baltimore",nickname:"Ravens"}, BUF:{city:"Buffalo",nickname:"Bills"},
  CAR:{city:"Carolina",nickname:"Panthers"}, CHI:{city:"Chicago",nickname:"Bears"},
  CIN:{city:"Cincinnati",nickname:"Bengals"}, CLE:{city:"Cleveland",nickname:"Browns"},
  DAL:{city:"Dallas",nickname:"Cowboys"}, DEN:{city:"Denver",nickname:"Broncos"},
  DET:{city:"Detroit",nickname:"Lions"}, GB:{city:"Green Bay",nickname:"Packers"},
  HOU:{city:"Houston",nickname:"Texans"}, IND:{city:"Indianapolis",nickname:"Colts"},
  JAX:{city:"Jacksonville",nickname:"Jaguars"}, KC:{city:"Kansas City",nickname:"Chiefs"},
  LAC:{city:"Los Angeles",nickname:"Chargers"}, LAR:{city:"Los Angeles",nickname:"Rams"},
  LV:{city:"Las Vegas",nickname:"Raiders"}, MIA:{city:"Miami",nickname:"Dolphins"},
  MIN:{city:"Minnesota",nickname:"Vikings"}, NE:{city:"New England",nickname:"Patriots"},
  NO:{city:"New Orleans",nickname:"Saints"}, NYG:{city:"New York",nickname:"Giants"},
  NYJ:{city:"New York",nickname:"Jets"}, PHI:{city:"Philadelphia",nickname:"Eagles"},
  PIT:{city:"Pittsburgh",nickname:"Steelers"}, SEA:{city:"Seattle",nickname:"Seahawks"},
  SF:{city:"San Francisco",nickname:"49ers"}, TB:{city:"Tampa Bay",nickname:"Buccaneers"},
  TEN:{city:"Tennessee",nickname:"Titans"}, WAS:{city:"Washington",nickname:"Commanders"},
};
function sdPick(obj, variants, fallback = 0) {
  for (const v of variants) {
    if (obj == null) break;
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase().replace(/[^a-z0-9]/g,"") === v.toLowerCase().replace(/[^a-z0-9]/g,"")) {
        return obj[k];
      }
    }
  }
  return fallback;
}

/* --------------------- name/pos/team normalization ----------------- */
function sdNormName(s){return String(s||"").toLowerCase().replace(/\u2019/g,"'").replace(/\./g,"").replace(/,\s*(jr|sr)\b/g,"").replace(/\b(jr|sr)\b/g,"").replace(/[^a-z' -]/g,"").replace(/\s+/g," ").trim();}
function normTeam(s){ const t = String(s || "").toUpperCase().trim(); return t === "JAC" ? "JAX" : t === "WSH" ? "WAS" : t; }
function normPosRaw(s){return (s||"").toUpperCase().trim();}
function sdNormPos(s){ const p = normPosRaw(s); return ["D", "DEF", "DEFENSE", "D/ST", "DST"].includes(p) ? "DST" : p; }
function inferTeamFromNameForDST(name) {
  const nm = sdNormName(name);
  // Nicknames identify LA/NY defenses without ambiguous shared-city matches.
  for (const [abbr, team] of Object.entries(NFL_TEAMS)) {
    if (nm === sdNormName(team.nickname) || nm === sdNormName(`${team.city} ${team.nickname}`) || nm.includes(sdNormName(team.nickname))) return abbr;
  }
  const cities = Object.entries(NFL_TEAMS).filter(([, team]) => nm === sdNormName(team.city));
  return cities.length === 1 ? cities[0][0] : "";
}

// Shared data stays separate from site/slate-specific salary and upload IDs.

const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];

function showdownPlayerKey(name, team, pos = "") {
  const tm = normTeam(team);
  const dst = sdNormPos(pos) === "DST" || (tm && inferTeamFromNameForDST(name) === tm);
  return `${tm}|${dst ? "DST" : sdNormName(name)}`;
}

function availableShowdownSlates(data, site) {
  // Old JSON remains usable while the replacement exporter is being installed.
  const source = Array.isArray(data?.slates) ? data.slates
    : data && (data.dk?.length || data.fd?.length) ? [{ ...data, key: "legacy", label: "Current showdown" }] : [];
  return source.filter((s) => Array.isArray(s[site]) && s[site].length > 0)
    .slice().sort((a, b) => String(a.gameDate || "").localeCompare(String(b.gameDate || "")) || a.key.localeCompare(b.key));
}

// Interpret salary-file wall-clock timestamps in Eastern time, regardless of
// the user's browser timezone, including Eastern standard/daylight time.
function showdownKickoffMillis(slate) {
  const raw = String(slate?.gameDate || "").trim();
  if (!raw) return NaN;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return Date.parse(raw);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return NaN;
  const target = Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: slate.timezone || "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  });
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
    const displayed = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    const correction = target - displayed;
    if (!correction) return instant;
    instant += correction;
  }
  return instant;
}

function chooseShowdownSlate(slates, selection, now) {
  const choices = slates.map((slate) => ({ slate, kickoff: showdownKickoffMillis(slate) }))
    .sort((a, b) => (Number.isFinite(a.kickoff) ? a.kickoff : Infinity) - (Number.isFinite(b.kickoff) ? b.kickoff : Infinity));
  const manual = selection && choices.find(({ slate }) => slate.key === selection.key);
  // A future game selected manually advances once it locks. Selecting a game
  // that was already locked deliberately opens its history for review.
  if (manual && (!Number.isFinite(manual.kickoff) || now < manual.kickoff || selection.selectedAt >= manual.kickoff)) return manual.slate;
  const upcoming = choices.find(({ kickoff }) => Number.isFinite(kickoff) && kickoff > now);
  if (upcoming) return upcoming.slate;
  // If every game has locked, keep the latest game available for review.
  return choices.filter(({ kickoff }) => Number.isFinite(kickoff)).at(-1)?.slate || choices[0]?.slate || null;
}

function buildShowdownRows({ slate, site, simulations, projections, legacyIds, customProj }) {
  if (!slate) return [];
  const scheduleTeams = [slate.away, slate.home].filter(Boolean).map(normTeam);
  const records = slate[site] || [];
  const groups = new Map();
  for (const rec of records) {
    const tm = normTeam(rec.team);
    const k = showdownPlayerKey(rec.name, tm, rec.player_position || rec.pos);
    const g = groups.get(k) || { name: rec.name, team: tm, records: [] };
    g.records.push(rec); groups.set(k, g);
  }
  const index = (data) => new Map(rowsOf(data).map((r) => [
    showdownPlayerKey(r.player ?? r.name ?? r.Player, r.team ?? r.Team, r.pos ?? r.Pos), r,
  ]));
  const simMap = index(simulations), projMap = index(projections);
  const originalKeys = new Set((legacyIds?.[site] || []).map((r) => r.slate_key).filter(Boolean));
  // The old projections workbook only supplies rates for its original game.
  const originalGame = slate.key === "legacy" || originalKeys.has(slate.key);
  const pct = (v) => {
    if (v == null || String(v).trim() === "") return null;
    const n = numberLike(v);
    return String(v).includes("%") || n > 1 ? n / 100 : n;
  };
  const output = [];
  for (const [k, group] of groups) {
    const sim = simMap.get(k), old = originalGame ? projMap.get(k) : null;
    const flex = site === "dk" ? group.records.find((r) => r.pos === "FLEX") : group.records[0];
    const cap = site === "dk" ? group.records.find((r) => r.pos === "CPT") : flex;
    if (!flex || !cap || !flex.id || !cap.id) continue;
    const salary = numberLike(site === "dk" ? flex.salary : flex.salary_flex);
    const capSalary = numberLike(site === "dk" ? cap.salary : cap.salary_mvp);
    if (salary <= 0 || capSalary <= 0) continue;
    const source = sim || old || {};
    const name = sim?.player ?? old?.player ?? old?.Player ?? group.name;
    const pos = sdNormPos(source.pos ?? source.Pos ?? flex.player_position ?? flex.pos);
    const realPos = ["QB", "RB", "WR", "TE", "DST", "K"].includes(pos) ? pos : (k.endsWith("|DST") ? "DST" : "");
    const baseProj = numberLike(sdPick(source, [`${site}_proj`, `${site} proj`, `${SITES[site].flexFieldPrefix} proj`], 0));
    const floor = numberLike(sdPick(source, [`${site}_floor`, `${SITES[site].flexFieldPrefix} floor`], 0));
    const ceil = numberLike(sdPick(source, [`${site}_ceiling`, `${site}_ceil`, `${SITES[site].flexFieldPrefix} ceiling`], 0));
    const capTag = SITES[site].capTag;
    const slotRate = (field, isCap) => {
      if (!old) return null;
      const prefix = isCap ? SITES[site].mvpFieldPrefix : SITES[site].flexFieldPrefix;
      const tag = capTag.toLowerCase();
      const fields = isCap
        ? [`${site}_${tag}_${field}`, `${site}_${tag}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`]
        : [`${site}_${field}`, `${site}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`];
      return pct(sdPick(old, fields, null));
    };
    const customFlex = customProj?.[`${name}::FLEX`];
    const customCap = customProj?.[`${name}::${capTag}`];
    const hasFlexCustom = customFlex != null && String(customFlex).trim() !== "";
    const hasCapCustom = customCap != null && String(customCap).trim() !== "";
    // Captain/MVP receives 1.5x points. Salaries always come from the salary file.
    const baseCapProj = baseProj * 1.5;
    output.push({
      name, team: group.team, pos: realPos,
      opp: scheduleTeams.find((tm) => tm !== group.team) || source.opp || source.Opp || "",
      salary, cap_salary: capSalary,
      proj: hasFlexCustom ? Math.max(0, numberLike(customFlex)) : baseProj,
      cap_proj: hasCapCustom ? Math.max(0, numberLike(customCap)) : baseCapProj,
      base_proj: baseProj, base_cap_proj: baseCapProj,
      custom_proj: hasFlexCustom ? customFlex : "", custom_cap_proj: hasCapCustom ? customCap : "",
      has_custom_proj: hasFlexCustom, has_custom_cap_proj: hasCapCustom,
      floor, ceil, cap_floor: floor * 1.5, cap_ceil: ceil * 1.5,
      pown: slotRate("pown", false), opt: slotRate("opt", false),
      cap_pown: slotRate("pown", true), cap_opt: slotRate("opt", true),
      hasProjection: !!(sim || old) && baseProj > 0,
      siteIdOnly: !(sim || old) || baseProj <= 0,
      siteId: String(flex.id), capSiteId: String(cap.id),
      siteName: flex.name, capSiteName: cap.name,
      fdPrefix: slate.fd_prefix || flex.prefix || null,
      slateKey: slate.key, __raw: source,
    });
  }
  return output;
}


function exclusionKey(player) {
  return `${normName(player.name)}|${fixTeam(player.team)}|${normPos(player.pos)}`;
}
const OPTIMIZE_MODES = [
  { key: "proj", label: "Projection" },
  { key: "optimal", label: "Optimal %" },
  { key: "ownership", label: "Ownership" },
  { key: "floor", label: "Floor" },
  { key: "ceiling", label: "Ceiling" },
];
function playerForSlot(player, slot, config) {
  const mult = Boolean(slot.multiplier);
  const salary = mult ? player.multiplierSalary : player.salary;
  const proj = mult ? player.multiplierProj : player.proj;
  return { ...player, salary, proj,
    ownership: mult ? player.multiplierOwnership : player.ownership,
    optimal: mult ? player.multiplierOptimal : player.optimal,
    floor: player.floor * (mult ? 1.5 : 1), ceiling: player.ceiling * (mult ? 1.5 : 1),
    value: salary > 0 ? proj / (salary / 1000) : 0,
    siteId: mult ? player.multiplierSiteId : player.siteId,
    rosterTag: mult ? config.multiplierTag : "FLEX", baseSalary: player.salary, baseProj: player.proj };
}

// Exact salary/count/team dynamic program. Enumerate captain choices, then
// select distinct FLEX players; retain every nondominated cost/score state.
async function optimizeShowdownLineup(players, existing, config, metric, onProgress, cancelled = () => false) {
  if (!OPTIMIZE_MODES.some(mode => mode.key === metric)) throw new Error("Choose an optimization metric.");
  const open = config.slots.filter(slot => !existing[slot.id]);
  if (!open.length) throw new Error("Remove a player to open a roster spot first.");
  const locked = config.slots.map(slot => existing[slot.id]).filter(Boolean);
  const lockedKeys = new Set(locked.map(exclusionKey));
  if (lockedKeys.size !== locked.length) throw new Error("The lineup contains a duplicate player.");
  const lockedCost = locked.reduce((sum, p) => sum + p.salary, 0);
  if (lockedCost > config.cap) throw new Error("The selected players are already over the salary cap.");
  const teams = [...new Set(players.map(p => p.team).filter(Boolean))];
  if (teams.length !== 2 || locked.some(p => !teams.includes(p.team))) throw new Error("This showdown slate needs players from exactly two teams.");
  const bit = p => 1 << teams.indexOf(p.team);
  const rateMetric = metric === "ownership" || metric === "optimal";
  // Match Classic's geometric optimal-rate objective; other metrics use sums.
  const score = p => metric === "optimal" ? Math.log(p.optimal) : Number(p[metric]);
  const usable = p => p.salary > 0 && Number.isFinite(p.salary) && p.siteId &&
    p[metric] != null && Number.isFinite(Number(p[metric])) && (metric !== "optimal" || p.optimal > 0);
  if (locked.some(p => !usable(p))) throw new Error(rateMetric ? "A selected player is missing this slot's rate. Choose another metric." : "A selected player is missing salary or projection data.");
  const pool = [...new Map(players.filter(p => p.hasProjection && p.salary > 0 && !lockedKeys.has(exclusionKey(p)))
    .map(p => [exclusionKey(p), p])).values()];
  const capSlot = config.slots.find(slot => slot.multiplier);
  const capOpen = !existing[capSlot.id];
  const capChoices = capOpen ? pool.map(p => playerForSlot(p, capSlot, config)).filter(usable) : [null];
  if (!capChoices.length) throw new Error(rateMetric ? "No captain/MVP has this rate for the selected slate." : "No projected captain/MVP is available.");
  const flexSlots = open.filter(slot => !slot.multiplier);
  const flexSlot = config.slots.find(slot => !slot.multiplier);
  let best = null, work = 0;
  async function yieldIfNeeded(force = false) {
    if (cancelled()) throw new Error("Optimization cancelled because the slate changed.");
    if (force || ++work % 4000 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
      if (cancelled()) throw new Error("Optimization cancelled because the slate changed.");
    }
  }
  for (let ci = 0; ci < capChoices.length; ci++) {
    onProgress?.(`Optimizing ${ci + 1}/${capChoices.length}`);
    await yieldIfNeeded(true);
    const captain = capChoices[ci];
    const fixed = captain ? [...locked, captain] : locked;
    const fixedCost = fixed.reduce((sum, p) => sum + p.salary, 0);
    const budget = config.cap - fixedCost;
    if (budget < 0) continue;
    const fixedMask = fixed.reduce((mask, p) => mask | bit(p), 0);
    const states = Array.from({ length: flexSlots.length + 1 }, () => Array.from({ length: 4 }, () => new Map()));
    states[0][fixedMask].set(0, { value: 0, chosen: [] });
    const choices = pool.filter(p => !captain || exclusionKey(p) !== exclusionKey(captain))
      .map(p => playerForSlot(p, flexSlot, config)).filter(usable);
    for (const p of choices) {
      for (let count = flexSlots.length - 1; count >= 0; count--) {
        for (let mask = 0; mask < 4; mask++) {
          const target = states[count + 1][mask | bit(p)];
          for (const [cost, state] of states[count][mask]) {
            const nextCost = cost + p.salary;
            if (nextCost <= budget) {
              const value = state.value + score(p);
              if (!target.has(nextCost) || value > target.get(nextCost).value) target.set(nextCost, { value, chosen: [...state.chosen, p] });
            }
            await yieldIfNeeded();
          }
        }
      }
      // A cheaper state with an equal/higher score always dominates, for the
      // same number of players and team mask, after this player is processed.
      for (const masks of states) for (const costs of masks) {
        let high = -Infinity;
        for (const [cost, state] of [...costs].sort((a, b) => a[0] - b[0])) {
          if (state.value <= high) costs.delete(cost); else high = state.value;
        }
      }
    }
    const fixedScore = fixed.reduce((sum, p) => sum + score(p), 0);
    for (const [cost, state] of states[flexSlots.length][3]) {
      const value = fixedScore + state.value, salary = fixedCost + cost;
      if (!best || value > best.value || (value === best.value && salary < best.salary)) best = { value, salary, captain, chosen: state.chosen };
    }
  }
  if (!best) throw new Error("No valid six-player showdown lineup fits these selections and exclusions under the salary cap. Use players from both teams.");
  const result = { ...existing };
  if (best.captain) result[capSlot.id] = best.captain;
  flexSlots.forEach((slot, i) => { result[slot.id] = best.chosen[i]; });
  return result;
}

function useStableLastUpdated({ dataUrl, metaUrl, artifactEndsWith } = {}) {
  const [d, setD] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Prefer Last-Modified on the data file itself
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 2) Try GET (some hosts omit LM on HEAD)
      try {
        const r = await fetch(`${dataUrl}?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 3) Fallback: meta.json fields (global or artifact-specific)
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then(x => x.json());
        let iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.last_updated || null;

        if (!iso && Array.isArray(m?.artifacts) && artifactEndsWith) {
          const art = m.artifacts.find(a => String(a.path || "").endsWith(artifactEndsWith));
          iso = art?.last_updated || art?.tags?.last_updated || null;
        }

        if (alive && iso) {
          const dt = new Date(iso);
          if (!isNaN(dt)) setD(dt);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl, artifactEndsWith]);

  return d;
}

export default function NFLShowdownLineupLab() {
  const updatedAt = useStableLastUpdated({
    dataUrl: PROJECTIONS_URL,
    metaUrl: PROJECTIONS_URL.replace(/[^/]+$/, "meta.json"),
    artifactEndsWith: PROJECTIONS_URL,
  });
  const [site, setSite] = useStickyState("nflShowdownLineupLab.site", "dk");
  const [gameLogYear, setGameLogYear] = useStickyState(
    "nflShowdownLineupLab.gameLogYear",
    DEFAULT_GAME_LOG_YEAR
  );
  const [slateSelection, setSlateSelection] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const [lineups, setLineups] = useStickyState("nflShowdownLineupLab.lineupsBySlate.v2", {
    dk: { main: {}, early: {}, late: {} },
    fd: { main: {}, early: {}, late: {} },
  });
  const [exclusions, setExclusions] = useStickyState("nflShowdownLineupLab.exclusionsBySlate.v2", {});
  const [optimizeMode, setOptimizeMode] = useState("proj");
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState("");
  const [optimizeError, setOptimizeError] = useState("");
  const solveToken = useRef(0);
  const [simulationRows, setSimulationRows] = useState([]);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("ALL");
  const [sort, setSort] = useState({ key: "salary", direction: "desc" });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [hideUnprojected, setHideUnprojected] = useState(true);
  const [selectedGames, setSelectedGames] = useState(() => new Set());
  const [rowsRaw, setRowsRaw] = useState([]);
  const [vegasRowsRaw, setVegasRowsRaw] = useState([]);
  const [siteIdsRaw, setSiteIdsRaw] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const poolScrollRef = useRef(null);

  const isBoth = site === "both";
  const activeSite = site === "fd" ? "fd" : "dk";
  const config = SITE_CONFIG[activeSite];
  const SLATES = useMemo(() => site === "both"
    ? [...new Map([ ...availableShowdownSlates(siteIdsRaw, "dk"), ...availableShowdownSlates(siteIdsRaw, "fd") ].map(s => [s.key, s])).values()]
    : availableShowdownSlates(siteIdsRaw, activeSite), [siteIdsRaw, site, activeSite]);
  const activeSlate = chooseShowdownSlate(SLATES, slateSelection?.site === site ? slateSelection : null, now);
  const slate = activeSlate?.key || "none";
  const setSlate = (key) => setSlateSelection({ site, key, selectedAt: Date.now() });
  const scopeRef = useRef("");
  scopeRef.current = `${site}|${slate}`;
  useEffect(() => {
    solveToken.current += 1;
    setOptimizing(false);
    setOptimizeError("");
    setOptimizeProgress("");
    return () => { solveToken.current += 1; };
  }, [site, slate]);
  const excludedKeys = useMemo(() => new Set(exclusions?.[activeSite]?.[slate] || []), [exclusions, activeSite, slate]);
  const updateExclusions = (keys) => setExclusions(current => ({ ...current, [activeSite]: { ...current?.[activeSite], [slate]: keys } }));
  const storedLineup = isBoth ? {} : lineups?.[activeSite]?.[slate] || {};


  const updateActiveLineup = (updater) => {
    if (isBoth) return;
    setLineups((current) => {
      const base = current && typeof current === "object" ? current : {};
      const siteStore = base?.[activeSite] && typeof base[activeSite] === "object" ? base[activeSite] : {};
      const existing = siteStore?.[slate] && typeof siteStore[slate] === "object" ? siteStore[slate] : {};
      const next = typeof updater === "function" ? updater(existing) : updater;
      return {
        ...base,
        [activeSite]: {
          main: siteStore.main || {},
          early: siteStore.early || {},
          late: siteStore.late || {},
          [slate]: next,
        },
      };
    });
  };

  // Current-slate files must always bypass browser and module caches so replacing
  // projections.json/site_ids.json/vegas.json is reflected immediately.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetchRows(PLAYER_SIMULATIONS_URL, { fresh: true }),
      fetchRows(PROJECTIONS_URL, { fresh: true }).catch(() => []),
      fetchRows(VEGAS_URL, { fresh: true }),
      fetchJsonFresh(SITE_IDS_URL),
    ])
      .then(([simRows, projectionRows, vegasRows, siteIdsJson]) => {
        if (!alive) return;
        setSimulationRows(simRows);
        setRowsRaw(projectionRows);
        setVegasRowsRaw(vegasRows);
        setSiteIdsRaw(siteIdsJson && typeof siteIdsJson === "object" ? siteIdsJson : {});
      })
      .catch((loadError) => {
        if (!alive) return;
        setSimulationRows([]);
        setRowsRaw([]);
        setVegasRowsRaw([]);
        setSiteIdsRaw({});
        setError(String(loadError));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Do not pre-build every position on page load. Preparing all four game-log
  // datasets at once can monopolize the main thread and make the page appear frozen.
  // Logs are prepared only when a player is opened, then cached for that position.

  useEffect(() => {
    setSelectedSlot(null);
  }, [site, slate]);

  useEffect(() => {
    setSort(isBoth ? { key: "dkSalary", direction: "desc" } : { key: "salary", direction: "desc" });
    setSelectedGames(new Set());
  }, [isBoth, activeSite, slate]);

  useEffect(() => {
    if (!profile) return undefined;

    let alive = true;
    const posKey = String(profile.pos || "QB").toUpperCase();

    setLogs([]);
    setLogsLoading(true);
    setLogsError("");

    loadPreparedGameLogs(posKey, gameLogYear)
      .then((prepared) => {
        if (alive) setLogs(prepared);
      })
      .catch((loadError) => {
        if (alive) setLogsError(String(loadError));
      })
      .finally(() => {
        if (alive) setLogsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [profile, gameLogYear]);

  const players = useMemo(() => {
    const bySite = {};
    for (const key of ["dk", "fd"]) {
      bySite[key] = new Map(buildShowdownRows({ slate: activeSlate, site: key,
        simulations: simulationRows, projections: rowsRaw, legacyIds: siteIdsRaw, customProj: {} })
        .map(r => [showdownPlayerKey(r.name, r.team, r.pos), r]));
    }
    const keys = isBoth ? new Set([...bySite.dk.keys(), ...bySite.fd.keys()]) : new Set(bySite[activeSite].keys());
    const siteView = (r) => ({ available: !!r, salary: r?.salary || 0,
      multiplierSalary: r?.cap_salary || 0, siteId: r?.siteId || "", multiplierSiteId: r?.capSiteId || "",
      proj: r?.proj || 0, multiplierProj: r?.cap_proj || 0,
      ownership: r?.pown ?? null, multiplierOwnership: r?.cap_pown ?? null,
      optimal: r?.opt ?? null, multiplierOptimal: r?.cap_opt ?? null,
      floor: r?.floor || 0, ceiling: r?.ceil || 0 });
    const stamp = showdownKickoffMillis(activeSlate);
    const time = Number.isFinite(stamp) ? new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit",
    }).format(new Date(stamp)) + " ET" : "";
    return [...keys].map(key => {
      const r = bySite[activeSite].get(key) || bySite.dk.get(key) || bySite.fd.get(key);
      const dk = siteView(bySite.dk.get(key)), fd = siteView(bySite.fd.get(key));
      const active = activeSite === "fd" ? fd : dk;
      return { id: key, name: r.name, pos: r.pos, team: r.team, opp: r.opp, time,
        pairKey: [r.team, r.opp].filter(Boolean).sort().join("@"), raw: r.__raw, dk, fd,
        dkSalary: dk.salary, fdSalary: fd.salary, dkProj: dk.proj, fdProj: fd.proj,
        dkOwnership: dk.ownership, fdOwnership: fd.ownership, dkOptimal: dk.optimal, fdOptimal: fd.optimal,
        ...active, hasProjection: r.hasProjection, unprojected: !r.hasProjection,
        value: active.salary > 0 ? active.proj / (active.salary / 1000) : 0 };
    }).filter(p => p.name && p.pos);
  }, [simulationRows, rowsRaw, siteIdsRaw, activeSlate, activeSite, isBoth]);
  useEffect(() => {
    if (["ownership", "optimal"].includes(optimizeMode) && !players.some(p =>
      p[optimizeMode] != null || p[optimizeMode === "ownership" ? "multiplierOwnership" : "multiplierOptimal"] != null)) {
      setOptimizeMode("proj");
    }
  }, [players, optimizeMode]);
  const lineup = useMemo(() => {
    const byId = new Map(players.map(p => [p.id, p]));
    return Object.fromEntries(config.slots.flatMap(slot => {
      const old = storedLineup[slot.id];
      const player = old && byId.get(old.id);
      return player && player.available ? [[slot.id, playerForSlot(player, slot, config)]] : [];
    }));
  }, [players, storedLineup, config]);
  const selectedPlayers = useMemo(() => Object.values(lineup).filter(Boolean), [lineup]);
  const selectedIds = useMemo(() => {
    const source = isBoth
      ? [
          ...Object.values(lineups?.dk?.[slate] || {}),
          ...Object.values(lineups?.fd?.[slate] || {}),
        ]
      : selectedPlayers;
    return new Set(source.filter(Boolean).map((p) => p.id));
  }, [isBoth, lineups, selectedPlayers, slate]);

  // Build team-level betting context from vegas.json.
  // projections.json remains only as a fallback for a missing matchup row.
  const teamVegasMap = useMemo(() => {
    const map = new Map();

    const absorbProjectionFallback = (row) => {
      const team = fixTeam(firstValue(row, ["Team", "team", "Tm", "TEAM"], ""));
      const opp = fixTeam(firstValue(row, ["OPP", "Opp", "opp", "Opponent", "opponent"], ""));
      if (!team) return;

      const previous = map.get(team) || {};
      const impliedRaw = firstValue(
        row,
        ["Imp Total", "ImpTotal", "Implied Total", "Team Total", "implied", "Implied"],
        null
      );
      const ouRaw = firstValue(row, ["O/U", "OU", "ou", "Game Total", "Total"], null);
      const spreadRaw = firstValue(row, ["Spread", "spread", "Line", "line", "Spr", "spr"], null);
      const confirmedHome = getHomeTeam(row);

      map.set(team, {
        ...previous,
        opponent: previous.opponent || opp,
        implied:
          previous.implied ??
          (impliedRaw == null || impliedRaw === "" ? null : numberLike(impliedRaw)),
        ou:
          previous.ou ??
          (ouRaw == null || ouRaw === "" ? null : numberLike(ouRaw)),
        spread:
          previous.spread ??
          (spreadRaw == null || spreadRaw === "" ? null : numberLike(spreadRaw)),
        isHome:
          previous.isHome ??
          (confirmedHome ? confirmedHome === team : null),
      });
    };

    // Low-priority fallback.
    for (const row of rowsRaw) absorbProjectionFallback(row);

    // vegas.json is authoritative and overwrites fallback values.
    for (const row of vegasRowsRaw) {
      const team = fixTeam(firstValue(row, ["team", "Team"], ""));
      const opponent = fixTeam(firstValue(row, ["opponent", "Opponent", "opp", "Opp"], ""));
      const field = fixTeam(firstValue(row, ["field", "Field", "home", "Home"], ""));

      if (!team) continue;

      const teamTotalRaw = firstValue(
        row,
        ["team_total", "Team Total", "teamTotal", "Imp Total"],
        null
      );
      const totalRaw = firstValue(
        row,
        ["over_under", "O/U", "OU", "game_total"],
        null
      );
      const spreadRaw = firstValue(row, ["spread", "Spread"], null);
      const moneylineRaw = firstValue(row, ["moneyline", "Moneyline", "ML"], null);
      const gameDate = firstValue(row, ["gameDate", "game_date", "Game Date"], "");

      map.set(team, {
        opponent,
        implied:
          teamTotalRaw == null || teamTotalRaw === ""
            ? null
            : numberLike(teamTotalRaw),
        ou:
          totalRaw == null || totalRaw === ""
            ? null
            : numberLike(totalRaw),
        spread:
          spreadRaw == null || spreadRaw === ""
            ? null
            : numberLike(spreadRaw),
        moneyline:
          moneylineRaw == null || moneylineRaw === ""
            ? null
            : numberLike(moneylineRaw),
        isHome: field ? field === team : null,
        field,
        gameDate,
      });
    }

    return map;
  }, [rowsRaw, vegasRowsRaw]);

  const teamImpliedMap = useMemo(() => {
    const map = new Map();
    for (const [team, values] of teamVegasMap.entries()) {
      if (Number.isFinite(values?.implied)) map.set(team, values.implied);
    }
    return map;
  }, [teamVegasMap]);

  const games = useMemo(() => {
    const byGame = new Map();

    for (const player of players) {
      if (!player.pairKey || !player.team || !player.opp) continue;

      if (!byGame.has(player.pairKey)) {
        byGame.set(player.pairKey, {
          gameKey: player.pairKey,
          teams: [player.team, player.opp],
          time: player.time || "",
          kick: parseKickoffMinutes(player.time),
        });
      } else {
        const current = byGame.get(player.pairKey);
        const kick = parseKickoffMinutes(player.time);
        if (kick < current.kick) {
          current.kick = kick;
          current.time = player.time || current.time;
        }
      }
    }

    return Array.from(byGame.values())
      .map((game) => {
        const teamA = game.teams?.[0] || "";
        const teamB = game.teams?.[1] || "";
        const aVegas = teamVegasMap.get(teamA) || {};
        const bVegas = teamVegasMap.get(teamB) || {};

        let away = teamA;
        let home = teamB;

        // Always place the confirmed home team second/bottom.
        if (aVegas.isHome === true && bVegas.isHome !== true) {
          home = teamA;
          away = teamB;
        } else if (bVegas.isHome === true && aVegas.isHome !== true) {
          home = teamB;
          away = teamA;
        } else if (aVegas.isHome === false && bVegas.isHome !== false) {
          away = teamA;
          home = teamB;
        } else if (bVegas.isHome === false && aVegas.isHome !== false) {
          away = teamB;
          home = teamA;
        }

        return {
          ...game,
          away,
          home,
          awayImplied: teamImpliedMap.get(away),
          homeImplied: teamImpliedMap.get(home),
          // If the feeds omit Spread, derive it from the two implied totals.
          // Team spread = opponent implied total - team implied total.
          awaySpread: Number.isFinite(teamVegasMap.get(away)?.spread)
            ? teamVegasMap.get(away).spread
            : Number.isFinite(teamImpliedMap.get(away)) && Number.isFinite(teamImpliedMap.get(home))
            ? teamImpliedMap.get(home) - teamImpliedMap.get(away)
            : null,
          homeSpread: Number.isFinite(teamVegasMap.get(home)?.spread)
            ? teamVegasMap.get(home).spread
            : Number.isFinite(teamImpliedMap.get(away)) && Number.isFinite(teamImpliedMap.get(home))
            ? teamImpliedMap.get(away) - teamImpliedMap.get(home)
            : null,
          gameTotal: teamVegasMap.get(away)?.ou ?? teamVegasMap.get(home)?.ou,
          awayMoneyline: teamVegasMap.get(away)?.moneyline ?? null,
          homeMoneyline: teamVegasMap.get(home)?.moneyline ?? null,
        };
      })
      .sort((a, b) => {
        const ak = a.kick ?? Number.POSITIVE_INFINITY;
        const bk = b.kick ?? Number.POSITIVE_INFINITY;
        if (ak !== bk) return ak - bk;
        return a.gameKey.localeCompare(b.gameKey);
      });
  }, [players, teamImpliedMap, teamVegasMap]);

  const toggleGame = (gameKey) => {
    setSelectedGames((current) => {
      const next = new Set(current);
      next.has(gameKey) ? next.delete(gameKey) : next.add(gameKey);
      return next;
    });
  };

  const filteredPlayers = useMemo(() => {
    const selectedSlotMeta = config.slots.find((slot) => slot.id === selectedSlot);
    const query = search.trim().toLowerCase();
    const result = players.filter((player) => {
      if (!isBoth && player.salary <= 0) return false;
      if (position === "FLEX" && !["RB", "WR", "TE"].includes(player.pos)) return false;
      if (position !== "ALL" && position !== "FLEX" && player.pos !== position) return false;
      if (selectedSlotMeta && !selectedSlotMeta.eligible.includes(player.pos)) return false;
      if (hideUnprojected && player.unprojected) return false;
      if (selectedGames.size > 0 && !selectedGames.has(player.pairKey)) return false;
      if (
        query &&
        !`${player.name} ${player.team} ${player.opp} ${player.pos}`.toLowerCase().includes(query)
      )
        return false;
      return true;
    });
    const sign = sort.direction === "asc" ? 1 : -1;
    return result.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "string") return av.localeCompare(bv) * sign;
      return ((Number(av) || 0) - (Number(bv) || 0)) * sign;
    });
  }, [players, position, selectedSlot, config.slots, search, hideUnprojected, selectedGames, sort, isBoth]);

  const totals = useMemo(() => {
    const salary = selectedPlayers.reduce((sum, p) => sum + p.salary, 0);
    const projection = selectedPlayers.reduce((sum, p) => sum + p.proj, 0);
    const ownComplete = selectedPlayers.length > 0 && selectedPlayers.every(p => p.ownership != null);
    const ownership = ownComplete ? selectedPlayers.reduce((sum, p) => sum + p.ownership, 0) : null;
    const geometricOwnership = ownComplete ? geometricMean(selectedPlayers.map(p => p.ownership)) : null;
    const optimal = selectedPlayers.length > 0 && selectedPlayers.every(p => p.optimal != null) ? geometricMean(selectedPlayers.map(p => p.optimal)) : null;
    const floor = selectedPlayers.reduce((sum, p) => sum + p.floor, 0);
    const ceiling = selectedPlayers.reduce((sum, p) => sum + p.ceiling, 0);
    const remaining = config.cap - salary;
    const openSlots = config.slots.length - selectedPlayers.length;
    const averageLeft = openSlots ? remaining / openSlots : 0;
    const complete = openSlots === 0;
    const teamCounts = selectedPlayers.reduce((map, player) => {
      if (player.team) map.set(player.team, (map.get(player.team) || 0) + 1);
      return map;
    }, new Map());
    const usesBothTeams = teamCounts.size >= 2;
    const maxFromOneTeam = Math.max(0, ...teamCounts.values());
    const valid = complete && remaining >= 0 && usesBothTeams && maxFromOneTeam <= 5;
    return {
      salary,
      projection,
      ownership,
      geometricOwnership,
      optimal,
      floor,
      ceiling,
      remaining,
      openSlots,
      averageLeft,
      complete,
      valid,
      usesBothTeams,
      maxFromOneTeam,
      teamCounts,
    };
  }, [selectedPlayers, config]);

  const lastOpenSlot = useMemo(() => {
    if (isBoth || totals.openSlots !== 1) return null;
    return config.slots.find((slot) => !lineup[slot.id]) || null;
  }, [isBoth, totals.openSlots, config.slots, lineup]);

  const maxAffordableSalary = lastOpenSlot ? Math.max(0, totals.remaining) : null;

  useEffect(() => {
    if (!lastOpenSlot) return;

    setSelectedSlot(lastOpenSlot.id);
    setSort({ key: "salary", direction: "desc" });

    setPosition("ALL");
  }, [lastOpenSlot]);

  useEffect(() => {
    if (!lastOpenSlot || !poolScrollRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      const rows = Array.from(
        poolScrollRef.current?.querySelectorAll("[data-player-salary]") || []
      );
      const target = rows.find(
        (row) => Number(row.dataset.playerSalary) <= maxAffordableSalary
      );

      if (target) {
        target.scrollIntoView({ block: "start", behavior: "smooth" });
      } else {
        poolScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [lastOpenSlot, maxAffordableSalary, filteredPlayers]);

  function findOpenSlot(player) {
    if (selectedSlot) {
      const selectedMeta = config.slots.find((slot) => slot.id === selectedSlot);
      if (selectedMeta && !lineup[selectedSlot] && selectedMeta.eligible.includes(player.pos)) return selectedSlot;
    }
    return config.slots.find((slot) => !lineup[slot.id] && slot.eligible.includes(player.pos))?.id || null;
  }

  function addPlayer(player) {
    if (isBoth || optimizing || excludedKeys.has(exclusionKey(player))) return;

    // The pool button acts as a true toggle:
    // clicking a checked player removes that player from every occupied slot.
    if (selectedIds.has(player.id)) {
      updateActiveLineup((current) => {
        const next = { ...current };

        for (const [slotId, selectedPlayer] of Object.entries(next)) {
          if (selectedPlayer?.id === player.id) {
            delete next[slotId];
          }
        }

        return next;
      });
      setSelectedSlot(null);
      return;
    }

    const slotId = findOpenSlot(player);
    if (!slotId) return;
    const slotMeta = config.slots.find((slot) => slot.id === slotId);
    const selectedPlayer = playerForSlot(player, slotMeta, config);
    if (lastOpenSlot && selectedPlayer.salary > maxAffordableSalary) return;
    updateActiveLineup((current) => ({ ...current, [slotId]: selectedPlayer }));
    setSelectedSlot(null);
  }

  function removeSlot(slotId) {
    if (optimizing) return;
    updateActiveLineup((current) => {
      const next = { ...current };
      delete next[slotId];
      return next;
    });
  }

  function toggleSort(key) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  function openProfile(player) {
    setProfile(player);
  }

  function toggleExclude(player) {
    if (isBoth || optimizing) return;
    const key = exclusionKey(player);
    const next = new Set(excludedKeys);
    if (next.has(key)) next.delete(key);
    else {
      next.add(key);
      updateActiveLineup(current => Object.fromEntries(Object.entries(current).filter(([, p]) => !p || exclusionKey(p) !== key)));
    }
    updateExclusions([...next]);
  }
  async function runOptimize() {
    if (isBoth || optimizing || loading) return;
    const token = ++solveToken.current;
    const scope = `${site}|${slate}`;
    setOptimizing(true); setOptimizeError(""); setOptimizeProgress("");
    try {
      const result = await optimizeShowdownLineup(players.filter(p => !excludedKeys.has(exclusionKey(p))), lineup, config, optimizeMode,
        text => { if (scopeRef.current === scope && solveToken.current === token) setOptimizeProgress(text); },
        () => scopeRef.current !== scope || solveToken.current !== token);
      if (scopeRef.current === scope && solveToken.current === token) { updateActiveLineup(result); setSelectedSlot(null); }
    } catch (err) {
      if (scopeRef.current === scope && solveToken.current === token) setOptimizeError(err.message || "Could not build a valid lineup.");
    } finally {
      if (scopeRef.current === scope && solveToken.current === token) { setOptimizing(false); setOptimizeProgress(""); }
    }
  }


  const validationMessage = totals.valid
    ? `Valid ${config.label} showdown lineup`
    : totals.remaining < 0
    ? `${fmtMoney(Math.abs(totals.remaining))} over the salary cap`
    : totals.openSlots > 0
    ? `${totals.openSlots} roster spot${totals.openSlots === 1 ? "" : "s"} remaining`
    : !totals.usesBothTeams
    ? "Use at least one player from each team"
    : totals.maxFromOneTeam > 5
    ? "Maximum five players from one team"
    : "Invalid showdown lineup";

  return (
    <div className="min-h-screen bg-slate-950 px-3 py-2 text-slate-100 md:px-4">
      <div className="mx-auto max-w-[1700px]">
        <header className="mb-3 space-y-3">
          <div>
            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-sky-400">NFL Showdown</div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-black tracking-tight md:text-2xl">Lineup Lab</h1>
              {updatedAt && (
                <span className="text-xs font-normal italic text-slate-100">
                  Updated: {updatedAt.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-400">
              Select players to keep, exclude anyone you want to avoid, then optimize the open spots.
            </p>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="inline-flex shrink-0 rounded-xl border border-slate-800 bg-slate-900 p-1">
              {["dk", "fd", "both"].map((key) => {
                const value = SITE_CONFIG[key];
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={optimizing}
                    onClick={() => setSite(key)}
                    className={`flex h-9 min-w-[58px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-bold transition ${
                      site === key ? "bg-slate-800 ring-1 ring-sky-400 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    }`}
                  >
                    {key === "both" ? (
                      <>
                        <img src={SITE_CONFIG.dk.logo} alt="DraftKings" className="h-5 max-w-[34px] object-contain" />
                        <span className="text-[10px] font-black">+</span>
                        <img src={SITE_CONFIG.fd.logo} alt="FanDuel" className="h-5 max-w-[30px] object-contain" />
                      </>
                    ) : (
                      <img src={value.logo} alt={value.label} className="h-6 max-w-[46px] object-contain" />
                    )}
                  </button>
                );
              })}
            </div>
            {!isBoth && (
              <div className="flex shrink-0 items-center gap-1.5">
                <label htmlFor="lineup-optimize-mode" className="sr-only">Optimize for</label>
                <select
                  id="lineup-optimize-mode"
                  disabled={optimizing}
                  value={optimizeMode}
                  onChange={(event) => setOptimizeMode(event.target.value)}
                  className="h-9 rounded-xl border border-slate-700 bg-slate-900 px-2 text-xs font-semibold text-slate-100"
                >
                  {OPTIMIZE_MODES.map((mode) => (
                    <option disabled={["ownership", "optimal"].includes(mode.key) && !players.some(p => p[mode.key] != null || p[mode.key === "ownership" ? "multiplierOwnership" : "multiplierOptimal"] != null)} key={mode.key} value={mode.key}>{mode.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={runOptimize}
                  disabled={loading || optimizing || !players.length || totals.openSlots === 0}
                  title="Keep the players already selected and fill the open slots"
                  className="h-9 rounded-xl bg-sky-500 px-3 text-xs font-bold text-slate-950 hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {optimizing ? optimizeProgress || "Optimizing…" : "Optimize"}
                </button>
                {excludedKeys.size > 0 && (
                  <button
                    type="button"
                    disabled={optimizing}
                    onClick={() => updateExclusions([])}
                    className="h-9 rounded-xl border border-rose-500/40 bg-rose-500/10 px-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/20"
                    title="Allow all excluded players again on this site and slate"
                  >
                    Clear {excludedKeys.size} excluded
                  </button>
                )}
              </div>
            )}
            <select aria-label="Showdown slate" value={slate} disabled={optimizing || !SLATES.length}
              onChange={event => setSlate(event.target.value)}
              className="h-9 min-w-0 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-xs font-semibold text-slate-100 sm:w-[340px] sm:max-w-full">
              {!SLATES.length && <option value="none">No showdown slates available</option>}
              {SLATES.map(option => <option key={option.key} value={option.key}>{option.label || option.key}</option>)}
            </select>
            <button
              type="button"
              onClick={() => {
                if (optimizing) return;
                if (isBoth) {
                  setLineups((current) => ({
                    ...(current || {}),
                    dk: { ...(current?.dk || {}), [slate]: {} },
                    fd: { ...(current?.fd || {}), [slate]: {} },
                  }));
                } else {
                  updateActiveLineup({});
                }
                setSelectedSlot(null);
              }}
              className="h-9 rounded-xl border border-slate-700 bg-slate-900 px-3 text-xs font-semibold text-slate-300 hover:border-slate-600 hover:text-white"
            >
              {isBoth ? "Clear both lineups" : "Clear lineup"}
            </button>
          </div>
        </header>

        {optimizeError && !isBoth && <div role="alert" className="mb-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{optimizeError}</div>}
        {games.length > 0 ? (
          <div className="mb-2 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/70 shadow-xl shadow-black/20">
            <div className="overflow-x-auto p-1.5 pb-2">
              <div className="flex min-w-max gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedGames(new Set())}
                  className={`flex w-[132px] shrink-0 flex-col justify-center rounded-lg border px-2.5 py-1.5 text-left transition ${
                    selectedGames.size === 0
                      ? "border-sky-400 bg-sky-500/15 ring-1 ring-sky-400/60"
                      : "border-slate-700 bg-slate-950/60 hover:border-slate-600 hover:bg-slate-900"
                  }`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-sky-400">Slate</span>
                  <span className="mt-0.5 whitespace-nowrap text-[13px] font-black text-white">All players</span>
                  <span className="mt-1 text-[10px] text-slate-500">{games.length} {games.length === 1 ? "matchup" : "matchups"}</span>
                </button>

                {games.map((game) => {
                  const active = selectedGames.has(game.gameKey);
                  const teamRow = (team, implied, spread) => (
                    <div className="flex items-center justify-between gap-1.5 rounded-md bg-slate-900/80 px-1.5 py-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <img
                          src={`${LOGO_BASE}/${fixTeam(team)}.${LOGO_EXT}`}
                          alt={team}
                          className="h-4 w-4 shrink-0 object-contain"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                        <span className="text-[11px] font-extrabold text-slate-100">{team}</span>
                        {Number.isFinite(Number(spread)) ? (
                          <span className="text-[8px] font-bold tabular-nums text-slate-500">
                            {fmtSpread(spread)}
                          </span>
                        ) : null}
                      </div>
                      <span className="text-[12px] font-black tabular-nums text-slate-200">
                        {Number.isFinite(implied) ? fmt1(implied) : "—"}
                      </span>
                    </div>
                  );

                  return (
                    <button
                      key={game.gameKey}
                      type="button"
                      onClick={() => toggleGame(game.gameKey)}
                      className={`w-[154px] shrink-0 rounded-lg border p-1.5 text-left transition ${
                        active
                          ? "border-sky-400 bg-sky-500/10 ring-1 ring-sky-400/50"
                          : "border-slate-700 bg-slate-950/60 hover:border-slate-600 hover:bg-slate-900"
                      }`}
                    >
                      <div className="mb-1 flex items-start justify-between gap-1 px-0.5">
                        <div>
                          <div className="text-[10px] font-extrabold text-slate-200">{game.time || "Time TBD"}</div>
                          <div className="text-[10px] font-semibold text-slate-500">
                            O/U {Number.isFinite(game.gameTotal) ? fmt1(game.gameTotal) : "—"}
                          </div>
                        </div>
                        <span
                          className={`grid h-4 w-4 place-items-center rounded border text-[9px] font-black ${
                            active
                              ? "border-sky-400 bg-sky-500 text-slate-950"
                              : "border-slate-700 text-transparent"
                          }`}
                        >
                          ✓
                        </span>
                      </div>
                      <div className="space-y-0.5">
                        {teamRow(game.away, game.awayImplied, game.awaySpread)}
                        {teamRow(game.home, game.homeImplied, game.homeSpread)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_400px]">
          <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl shadow-black/20">
            <div className="border-b border-slate-800 p-2.5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative flex-1">
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search player, team, opponent..."
                    className="h-9 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 pr-9 text-xs text-white outline-none placeholder:text-slate-600 focus:border-sky-500"
                  />
                  {search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-400">
                  <input
                    type="checkbox"
                    checked={hideUnprojected}
                    onChange={(event) => setHideUnprojected(event.target.checked)}
                    className="h-4 w-4 accent-sky-500"
                  />
                  Hide unprojected
                </label>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {POSITIONS.map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => {
                      setPosition(pos);
                      if (pos !== "ALL") setSelectedSlot(null);
                    }}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                      position === pos
                        ? "border-sky-400 bg-sky-500 text-slate-950"
                        : "border-slate-700 bg-slate-950/70 text-slate-400 hover:border-slate-600 hover:text-white"
                    }`}
                  >
                    {pos}
                  </button>
                ))}
                {!isBoth && selectedSlot ? (
                  <button
                    type="button"
                    onClick={() => setSelectedSlot(null)}
                    className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-bold text-amber-300"
                  >
                    Filling {config.slots.find((slot) => slot.id === selectedSlot)?.label} ×
                  </button>
                ) : null}
              </div>
            </div>

            {!isBoth && lastOpenSlot ? (
              <div className="flex items-center justify-between gap-3 border-b border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs">
                <div>
                  <span className="font-bold text-amber-300">Finish lineup:</span>
                  <span className="ml-2 text-slate-300">
                    Add {lastOpenSlot.label} for {fmtMoney(maxAffordableSalary)} or less
                  </span>
                </div>
                <span className="shrink-0 rounded-full border border-amber-400/30 bg-slate-950/40 px-2 py-1 font-bold text-amber-200">
                  Max {fmtMoney(maxAffordableSalary)}
                </span>
              </div>
            ) : null}

            <div
              ref={poolScrollRef}
              className="h-[calc(100vh-255px)] min-h-[360px] max-h-[610px] overflow-auto"
            >
              <table className={`w-full border-collapse text-xs ${isBoth ? "min-w-[760px]" : "min-w-[900px]"}`}>
                <thead className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur">
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                    <th className="sticky left-0 z-30 w-px whitespace-nowrap bg-slate-950 px-3 py-2 text-left shadow-[8px_0_12px_-10px_rgba(0,0,0,0.95)]">Player</th>
                    <th className="px-2 py-2 text-center">Matchup</th>
                    {(isBoth
                      ? [
                          ["dkSalary", "DK Sal"],
                          ["fdSalary", "FD Sal"],
                          ["dkProj", "DK Proj"],
                          ["fdProj", "FD Proj"],
                        ]
                      : [
                          ["salary", "Salary"],
                          ["proj", "Proj"],
                          ["value", "Value"],
                          ["ownership", "Own%"],
                          ["optimal", "Opt%"],
                          ["ceiling", "Ceil"],
                        ]
                    ).map(([key, label]) => (
                      <th key={key} className="px-2 py-2 text-right">
                        <button type="button" onClick={() => toggleSort(key)} className="hover:text-white">
                          {label} {sort.key === key ? (sort.direction === "desc" ? "▼" : "▲") : ""}
                        </button>
                      </th>
                    ))}
                    {!isBoth ? <><th className="w-14 px-2 py-2 text-center">Add</th><th className="px-2 py-2 text-center">Exclude</th></> : null}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={isBoth ? 6 : 10} className="px-4 py-16 text-center text-slate-500">Loading player pool…</td></tr>
                  ) : error ? (
                    <tr><td colSpan={isBoth ? 6 : 10} className="px-4 py-16 text-center text-rose-400">{error}</td></tr>
                  ) : filteredPlayers.length === 0 ? (
                    <tr><td colSpan={isBoth ? 6 : 10} className="px-4 py-16 text-center text-slate-500">No players match these filters.</td></tr>
                  ) : (
                    filteredPlayers.map((player, index) => {
                      const selected = selectedIds.has(player.id);
                      const excluded = !isBoth && excludedKeys.has(exclusionKey(player));
                      const targetSlot = config.slots.find((slot) => slot.id === selectedSlot) || lastOpenSlot || null;
                      const targetIsMultiplier = Boolean(targetSlot?.multiplier);
                      const poolSalary = targetIsMultiplier ? player.multiplierSalary : player.salary;
                      const poolProjection = targetIsMultiplier ? player.multiplierProj : player.proj;
                      const poolOwnership = targetIsMultiplier ? player.multiplierOwnership : player.ownership;
                      const poolOptimal = targetIsMultiplier ? player.multiplierOptimal : player.optimal;
                      const poolCeiling = targetIsMultiplier ? player.ceiling * 1.5 : player.ceiling;
                      const poolValue = poolSalary > 0 ? poolProjection / (poolSalary / 1000) : 0;
                      const eligibleForLastSlot =
                        !lastOpenSlot || lastOpenSlot.eligible.includes(player.pos);
                      const unaffordable =
                        !isBoth && Boolean(lastOpenSlot) &&
                        eligibleForLastSlot &&
                        poolSalary > maxAffordableSalary;
                      const canAdd =
                        !isBoth && !optimizing && !excluded && Boolean(findOpenSlot(player)) &&
                        !selected &&
                        eligibleForLastSlot &&
                        !unaffordable;
                      return (
                        <tr
                          key={player.id}
                          data-player-salary={isBoth ? Math.max(player.dkSalary || 0, player.fdSalary || 0) : poolSalary}
                          className={`border-b border-slate-800/70 transition ${
                            unaffordable
                              ? "bg-rose-950/25 opacity-55"
                              : selected
                              ? "bg-sky-500/5"
                              : index % 2
                              ? "bg-slate-950/20"
                              : "bg-transparent"
                          } ${unaffordable ? "hover:bg-rose-950/30" : "hover:bg-slate-800/50"}`}
                        >
                          <td className="sticky left-0 z-[5] w-px whitespace-nowrap bg-slate-950 px-3 py-1 shadow-[8px_0_12px_-10px_rgba(0,0,0,0.95)]">
                            <div className="flex items-center gap-2">
                              <TeamBadge team={player.team} />
                              <div className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() => openProfile(player)}
                                  className="block whitespace-nowrap text-left font-bold text-slate-100 underline decoration-slate-600 underline-offset-4 hover:text-sky-300"
                                >
                                  {player.name}
                                </button>
                                <div className="mt-0.5 text-[10px] text-slate-500">{player.pos}{player.time ? ` • ${player.time}` : ""}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-1 text-center text-slate-400">
                            <span className="font-semibold text-slate-300">{player.team}</span>
                            <span className="mx-1 text-slate-600">
                              {player.isHome === true ? "vs" : player.isHome === false ? "@" : "vs"}
                            </span>
                            {player.opp || "—"}
                          </td>
                          {isBoth ? (
                            <>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-slate-100">
                                {player.dkSalary > 0 ? fmtMoney(player.dkSalary) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-slate-100">
                                {player.fdSalary > 0 ? fmtMoney(player.fdSalary) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">
                                {player.dkProj > 0 ? fmt1(player.dkProj) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">
                                {player.fdProj > 0 ? fmt1(player.fdProj) : "—"}
                              </td>
                            </>
                          ) : (
                            <>
                              <td
                                className={`px-2 py-1 text-right font-semibold tabular-nums ${
                                  unaffordable ? "text-rose-400 line-through" : "text-slate-100"
                                }`}
                              >
                                {fmtMoney(poolSalary)}
                              </td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">{fmt1(poolProjection)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-lime-300">{fmt1(poolValue)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-amber-300">{fmtPct(poolOwnership)}</td>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-emerald-300">{fmtPct(poolOptimal)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-slate-300">{fmt1(poolCeiling)}</td>
                              <td className="px-2 py-1 text-center">
                                <button
                                  type="button"
                                  disabled={optimizing || excluded || (!selected && !canAdd)}
                                  onClick={() => addPlayer(player)}
                                  title={
                                    excluded
                                      ? "Excluded from lineup"
                                      : selected
                                      ? "Remove player"
                                      : unaffordable
                                      ? `Too expensive — max ${fmtMoney(maxAffordableSalary)}`
                                      : canAdd
                                      ? "Add player"
                                      : "No eligible slot available"
                                  }
                                  className={`inline-flex h-7 w-7 items-center justify-center rounded-lg border text-lg font-bold transition ${
                                    selected
                                      ? "border-sky-500/30 bg-sky-500/10 text-sky-400"
                                      : canAdd
                                      ? "border-sky-500 bg-sky-500 text-slate-950 hover:bg-sky-400"
                                      : unaffordable
                                      ? "cursor-not-allowed border-rose-500/30 bg-rose-950/30 text-rose-500/60"
                                      : "cursor-not-allowed border-slate-800 bg-slate-900 text-slate-700"
                                  }`}
                                >
                                  {excluded ? "×" : selected ? "✓" : "+"}
                                </button>
                              </td>
                              <td className="px-2 py-1 text-center">
                                <button
                                  type="button"
                                  disabled={optimizing}
                                  onClick={() => toggleExclude(player)}
                                  title={excluded ? "Allow player in optimizer" : "Exclude player from optimizer"}
                                  aria-pressed={excluded}
                                  className={`rounded-lg border px-2 py-1 text-[10px] font-bold transition ${
                                    excluded
                                      ? "border-rose-400 bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"
                                      : "border-slate-700 text-slate-400 hover:border-rose-500/50 hover:text-rose-300"
                                  }`}
                                >
                                  {excluded ? "Excluded" : "Exclude"}
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="xl:sticky xl:top-[60px] xl:self-start">
            {isBoth ? (
              <div className="space-y-2">
                {["dk", "fd"].map((siteKey) => {
                  const cfg = SITE_CONFIG[siteKey];
                  const savedLineup = lineups?.[siteKey]?.[slate] || {};
                  const savedPlayers = Object.values(savedLineup).filter(Boolean);
                  const salary = savedPlayers.reduce((sum, p) => sum + numberLike(p.salary), 0);
                  const projection = savedPlayers.reduce((sum, p) => sum + numberLike(p.proj), 0);
                  const remaining = cfg.cap - salary;
                  return (
                    <button
                      key={siteKey}
                      type="button"
                      onClick={() => setSite(siteKey)}
                      className="w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/95 text-left shadow-2xl shadow-black/30 transition hover:border-sky-500/60"
                    >
                      <div className="flex items-center justify-between gap-3 border-b border-slate-800 p-3">
                        <div className="flex items-center gap-2">
                          <img src={cfg.logo} alt={cfg.label} className="h-6 max-w-[55px] object-contain" />
                          <div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-400">{cfg.label}</div>
                            <div className="text-sm font-black text-white">Your lineup</div>
                          </div>
                        </div>
                        <div className="shrink-0 whitespace-nowrap rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-bold tabular-nums text-slate-300">
                          {savedPlayers.length} / {cfg.slots.length}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 p-3">
                        <MetricCard label="Salary" value={fmtMoney(salary)} />
                        <MetricCard label="Remaining" value={fmtMoney(remaining)} tone={remaining < 0 ? "bad" : "default"} />
                        <MetricCard label="Projection" value={fmt1(projection)} />
                      </div>
                      <div className="px-3 pb-3 text-[10px] text-slate-500">Click to edit this lineup.</div>
                    </button>
                  );
                })}
              </div>
            ) : (
            <div
              className={`overflow-hidden rounded-2xl border bg-slate-900/95 shadow-2xl shadow-black/30 transition ${
                totals.valid
                  ? "border-emerald-500/60 shadow-emerald-950/30"
                  : totals.remaining < 0
                  ? "border-rose-500/60 shadow-rose-950/30"
                  : "border-slate-800"
              }`}
            >
              <div className="border-b border-slate-800 p-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <img src={config.logo} alt={config.label} className="h-5 max-w-[52px] object-contain" />
                      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-400">{config.label}</span>
                    </div>
                    <h2 className="mt-1 text-base font-black leading-tight">Your lineup</h2>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{activeSlate?.label || "Showdown"}</p>
                  </div>
                  <div className="shrink-0 whitespace-nowrap rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-bold tabular-nums text-slate-300">
                    {selectedPlayers.length} / {config.slots.length}
                  </div>
                </div>

                <div className="mt-1.5 grid grid-cols-3 gap-1">
                  <MetricCard label="Salary" value={fmtMoney(totals.salary)} />
                  <MetricCard
                    label="Remaining"
                    value={fmtMoney(totals.remaining)}
                    tone={totals.remaining < 0 ? "bad" : "default"}
                  />
                  <MetricCard
                    label="Avg. Left"
                    value={totals.openSlots ? fmtMoney(totals.averageLeft) : "—"}
                    subtext={totals.openSlots ? `${totals.openSlots} open` : "Complete"}
                  />
                </div>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  <MetricCard label="Projection" value={fmt1(totals.projection)} />
                  <MetricCard
                    label="Ownership"
                    value={fmtPct(totals.ownership)}
                    subtext={`${fmtPct(totals.geometricOwnership)} geometric mean`}
                  />
                  <MetricCard label="Lineup Opt%" value={fmtPct(totals.optimal)} />
                </div>
              </div>

              <div className="divide-y divide-slate-800">
                {config.slots.map((slot) => {
                  const player = lineup[slot.id];
                  const active = selectedSlot === slot.id;
                  return (
                    <div
                      key={slot.id}
                      className={`grid min-h-[39px] grid-cols-[44px_minmax(0,1fr)_72px_28px] items-center gap-1.5 px-2.5 py-0.5 transition ${
                        active ? "bg-sky-500/10" : "hover:bg-slate-800/40"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => !player && setSelectedSlot(active ? null : slot.id)}
                        className={`rounded-md border px-1.5 py-1.5 text-[10px] font-black ${
                          active
                            ? "border-sky-400 bg-sky-500 text-slate-950"
                            : "border-slate-700 bg-slate-950 text-slate-400"
                        }`}
                      >
                        {slot.label}
                      </button>

                      {player ? (
                        <button type="button" onClick={() => openProfile(player)} className="min-w-0 text-left">
                          <div className="truncate text-xs font-bold text-white hover:text-sky-300">{player.name}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-500">
                            <TeamBadge team={player.team} />
                            <span>vs {player.opp || "—"}</span>
                            <span>•</span>
                            <span>{fmt1(player.proj)} pts</span>
                          </div>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSelectedSlot(active ? null : slot.id)}
                          className="text-left text-xs font-medium text-slate-600 hover:text-slate-300"
                        >
                          Add a player
                        </button>
                      )}

                      <div className="text-right">
                        {player ? (
                          <>
                            <div className="text-xs font-bold tabular-nums text-slate-200">{fmtMoney(player.salary)}</div>
                            <div className="text-[10px] tabular-nums text-emerald-400">{fmtPct(player.ownership)} pOWN</div>
                          </>
                        ) : (
                          <span className="text-slate-700">—</span>
                        )}
                      </div>

                      <button
                        type="button"
                        disabled={!player}
                        onClick={() => removeSlot(slot.id)}
                        className={`h-7 w-7 rounded-md border text-base ${
                          player
                            ? "border-slate-700 text-slate-500 hover:border-rose-500/50 hover:bg-rose-500/10 hover:text-rose-400"
                            : "cursor-default border-transparent text-transparent"
                        }`}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-800 p-3">
                <div
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                    totals.valid
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : totals.remaining < 0
                      ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                      : "border-slate-700 bg-slate-950 text-slate-400"
                  }`}
                >
                  <span className="text-xs font-bold">{validationMessage}</span>
                  <span className="text-lg">{totals.valid ? "✓" : totals.remaining < 0 ? "!" : "•"}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2">
                    <div className="text-slate-500">Floor</div>
                    <div className="mt-0.5 font-bold tabular-nums text-slate-200">{fmt1(totals.floor)}</div>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2">
                    <div className="text-slate-500">Ceiling</div>
                    <div className="mt-0.5 font-bold tabular-nums text-slate-200">{fmt1(totals.ceiling)}</div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </aside>
        </div>
      </div>

      {profile ? (
        <GameLogModal
          player={profile}
          preparedRows={logs}
          loading={logsLoading}
          error={logsError}
          year={gameLogYear}
          years={GAME_LOG_YEARS}
          onYearChange={setGameLogYear}
          onClose={() => setProfile(null)}
        />
      ) : null}
    </div>
  );
}
